/** Display and composition order: QB · RB · WR · TE */
export const POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const MAX_QB = 3;
const MAX_TE = 3;
const MIN_QB = 2;
const MIN_TE = 2;

const POSITION_COLORS = {
	QB: {
		bg: 'bg-blue-500',
		text: 'text-blue-700 dark:text-blue-200',
		border: 'border-blue-400 dark:border-blue-500',
		soft: 'bg-blue-50 dark:bg-blue-900'
	},
	RB: {
		bg: 'bg-green-500',
		text: 'text-green-700 dark:text-green-300',
		border: 'border-green-400 dark:border-green-500',
		soft: 'bg-green-50 dark:bg-green-900'
	},
	WR: {
		bg: 'bg-yellow-500',
		text: 'text-yellow-700 dark:text-yellow-200',
		border: 'border-yellow-400 dark:border-yellow-500',
		soft: 'bg-yellow-50 dark:bg-yellow-900'
	},
	TE: {
		bg: 'bg-purple-500',
		text: 'text-purple-700 dark:text-purple-200',
		border: 'border-purple-400 dark:border-purple-500',
		soft: 'bg-purple-50 dark:bg-purple-900'
	}
};

/** 18-round anchor targets (QB-RB-WR-TE). QB/TE never exceed 3. */
const ANCHOR_COMPOSITION_18 = {
	balanced: { QB: 3, RB: 5, WR: 8, TE: 2 },
	balancedEliteQB: { QB: 2, RB: 5, WR: 8, TE: 3 },
	balancedEliteTE: { QB: 3, RB: 5, WR: 9, TE: 2 },
	balancedEliteBoth: { QB: 2, RB: 5, WR: 9, TE: 2 },
	zeroRb: { QB: 3, RB: 6, WR: 7, TE: 2 }
};

/** Extreme Zero WR (spectrum ~0): 3-4-9-2 standard; 2-4-10-2 when elite QB or TE is on. */
const ZERO_WR_EXTREME_18 = {
	standard: { QB: 3, RB: 4, WR: 9, TE: 2 },
	elite: { QB: 2, RB: 4, WR: 10, TE: 2 }
};

const ANCHOR_META = {
	zeroWr: {
		label: 'Zero WR',
		shortLabel: 'Hero RB',
		spectrumLabel: 'Zero WR',
		philosophy:
			'Attack RB scarcity early when weekly workload and goal-line equity are priced in. WR depth is replaceable in PPR — you can manufacture WR2 production in rounds 5–9 while your league mates chase name value.',
		rules: [
			'Extreme Zero WR: open RB-RB-RB-RB (elite QB/TE can replace one early slot).',
			'18-round targets: 3-4-9-2 (standard) or 2-4-10-2 with elite QB/TE enabled.',
			'Cap at 3 QB and 3 TE — after the RB run, flood WR volume in the middle rounds.',
			'Do not add a 5th RB; handcuffs only if they replace your last RB slot.'
		]
	},
	balanced: {
		label: 'Balanced',
		shortLabel: 'BPA + structure',
		spectrumLabel: 'Balanced',
		philosophy:
			'Let value drive the first four rounds, but enforce roster geometry: one anchor RB, one anchor WR, and a flex profile that survives bye/injury weeks. You are optimizing for league-winning ceilings without creating a positional hole.',
		rules: [
			'Never leave rounds 1–4 without at least one RB and one WR you would start.',
			'Standard 18-round target (no elites): 3 QB · 5 RB · 8 WR · 2 TE.',
			'No elite TE: first TE pick 90–110 (~rounds 9–10, 12-team); backup TE after that window.',
			'No elite QB: QB1 pick 80–100; QB2 pick 130–150; QB3 pick 170–190 (12-team).',
			'RB run awareness: if 4+ RBs go in a round, pivot to WR value.'
		]
	},
	zeroRb: {
		label: 'Zero RB',
		shortLabel: 'Hero WR',
		spectrumLabel: 'Zero RB',
		philosophy:
			'Pay for WR target share and offensive environment early. RB injury and committee risk are baked into ADP — you harvest WR alpha in rounds 1–4, then weaponize the mid-round RB tier when the room overcorrects.',
		rules: [
			'Extreme Zero RB: open WR in rounds 1–6; start RB run round 7+; WR7 on your final pick (7 WR total).',
			'18-round anchor: 3 QB · 6 RB · 7 WR · 2 TE (3-6-7-2).',
			'Never exceed 3 QB or 3 TE — elite TE can replace one early WR slot in the hero window.',
			'After the WR run, prioritize RB volume; handcuffs and contingent backs over WR5+.'
		]
	},
	heroRb: {
		label: 'Hero RB',
		shortLabel: 'Hero RB',
		spectrumLabel: 'Hero RB',
		philosophy:
			'Pay for one true RB1 in round 1, then build WR/QB/TE volume through the gap window. Revisit RB after the pause — total RB volume stays high (6 RB) but spaced, not stacked.',
		rules: [
			'Round 1: lock hero RB1 only — do not take another RB until the revisit window opens.',
			'Extreme Hero RB (18 rounds): 3 QB · 6 RB · 7 WR · 2 TE.',
			'Gap length scales with slider: longer pause before RB2 when you are more Hero RB-leaning.',
			'Revisit rounds refocus RB; fill WR and QB/TE on schedule between anchor and revisit.'
		]
	},
	heroWr: {
		label: 'Hero WR',
		shortLabel: 'Hero WR',
		spectrumLabel: 'Hero WR',
		philosophy:
			'Pay for one true WR1 in round 1, then build RB/QB/TE volume through the gap window. Revisit WR after the pause — target 7 WR total with the hero spaced from WR2+.',
		rules: [
			'Round 1: lock hero WR1 only — do not take another WR until the revisit window opens.',
			'Extreme Hero WR (18 rounds): 3 QB · 6 RB · 7 WR · 2 TE.',
			'Gap length scales with slider: longer pause before WR2 when you are more Hero WR-leaning.',
			'Revisit rounds refocus WR; RB run timing follows balanced heuristics after the hero anchor.'
		]
	}
};

/** Hero-mode extreme (18 rounds): 3 QB · 6 RB · 7 WR · 2 TE (18 picks). */
const HERO_SKILL_EXTREME_18 = { QB: 3, RB: 6, WR: 7, TE: 2 };

export const CONSTRUCTION_MODES = ['zero', 'hero'];

export function normalizeConstructionMode(mode) {
	return mode === 'hero' ? 'hero' : 'zero';
}

/** UI slider value → engine value (zero mode is mirrored: left = Zero RB, right = Zero WR). */
export function toEngineSpectrumValue(spectrumValue, constructionMode) {
	const v = clamp(spectrumValue, 0, 100);
	return normalizeConstructionMode(constructionMode) === 'hero' ? v : 100 - v;
}

function isHeroConstructionMode(options = {}) {
	return normalizeConstructionMode(options.constructionMode) === 'hero';
}

function heroRbEmphasis(spectrumValue) {
	if (spectrumValue >= 50) return 0;
	return (50 - spectrumValue) / 50;
}

function heroWrEmphasis(spectrumValue) {
	if (spectrumValue <= 50) return 0;
	return (spectrumValue - 50) / 50;
}

/** Rounds to avoid the hero position after R1 (2 = subtle, 7 = extreme). */
function heroPauseRoundCount(strength) {
	return Math.round(lerp(2, 7, clamp(strength, 0, 1)));
}

/** First round allowed for the same position after the R1 hero anchor. */
function heroPauseUntilRound(strength, totalRounds) {
	const pauseRounds = heroPauseRoundCount(strength);
	return clamp(1 + pauseRounds + 1, 2, totalRounds);
}

function isHeroRbAnchorMeta(roundMeta) {
	if (!roundMeta) return false;
	return Boolean(roundMeta.isHeroRbAnchor || roundMeta.heroAnchorPos === 'RB');
}

function isHeroWrAnchorMeta(roundMeta) {
	if (!roundMeta) return false;
	return Boolean(roundMeta.isHeroWrAnchor || roundMeta.heroAnchorPos === 'WR');
}

function isHeroRbRevisitMeta(roundMeta) {
	if (!roundMeta) return false;
	return Boolean(roundMeta.isHeroRbRevisit || (roundMeta.isHeroRevisit && roundMeta.heroRevisitPos === 'RB'));
}

function isHeroWrRevisitMeta(roundMeta) {
	if (!roundMeta) return false;
	return Boolean(roundMeta.isHeroWrRevisit || (roundMeta.isHeroRevisit && roundMeta.heroRevisitPos === 'WR'));
}

/** One label for round map / chips: hero gap revisits and post–elite revisits. */
function revisitPositionLabel(primary, pickMeta = {}) {
	if (
		!pickMeta ||
		(!pickMeta.isRevisitRound && !pickMeta.isHeroRbRevisit && !pickMeta.isHeroWrRevisit)
	) {
		return null;
	}
	if (pickMeta.isHeroWrRevisit || (pickMeta.isRevisitRound && primary === 'WR')) return 'Revisit WR';
	if (pickMeta.isHeroRbRevisit || (pickMeta.isRevisitRound && primary === 'RB')) return 'Revisit RB';
	if (pickMeta.isRevisitRound && primary === 'QB') return 'Revisit QB';
	if (pickMeta.isRevisitRound && primary === 'TE') return 'Revisit TE';
	return 'Revisit';
}

const TIER_GUIDANCE = {
	RB: {
		high: 'RB1 / three-down volume',
		mid: 'RB2 / high-upside committee or pass-down role',
		low: 'contingent back / handcuff with path to lead role'
	},
	WR: {
		high: 'WR1 / alpha target share',
		mid: 'WR2 / weekly flex with spike weeks',
		low: 'high-upside WR3 / ambiguous depth chart'
	},
	TE: {
		high: 'TE — premium early path (non-elite build)',
		starter: 'TE1 — solid starter with weekly floor or spike ceiling',
		mid: 'TE2 / streaming TE with red-zone role',
		low: 'late TE with athletic profile or vacated targets'
	},
	QB: {
		high: 'top-6 QB with rushing floor',
		mid: 'starting qb volume with high job security',
		low: 'late QB with rushing upside or stack correlation'
	}
};

const ROUND_PHASES = [
	{ maxRoundRatio: 0.2, phase: 'early', label: 'Early' },
	{ maxRoundRatio: 0.45, phase: 'earlyMid', label: 'Early-mid' },
	{ maxRoundRatio: 0.65, phase: 'mid', label: 'Mid' },
	{ maxRoundRatio: 0.85, phase: 'lateMid', label: 'Late-mid' },
	{ maxRoundRatio: 1, phase: 'late', label: 'Late' }
];

export const ROUND_COUNT_OPTIONS = [15, 16, 17, 18, 19, 20];

/** Used to convert overall pick targets (e.g. 90–100) into round slots. */
const DEFAULT_LEAGUE_SIZE = 12;

/** Non-elite QB windows by slot (12-team overall picks). */
const NON_ELITE_QB_PICK_WINDOWS = [
	[80, 100],
	[130, 150],
	[170, 190]
];

/** Non-elite TE1: pick 90–110 (12-team); rounds 9–10 on 18-round drafts. */
const NON_ELITE_TE_FIRST_PICK_RANGE = [90, 110];
const NON_ELITE_TE_FIRST_ROUND_RANGE_18 = [9, 10];

/** Late TE: all TE picks in rounds 12–18 (18-round anchor), biased toward the late half. */
const LATE_TE_ROUND_RANGE_18 = [12, 18];
const LATE_TE_PICK_COUNT = 3;

/** Late QB: all QB picks in rounds 12–18 (18-round anchor), biased toward the early half. */
const LATE_QB_ROUND_RANGE_18 = [12, 18];
const LATE_QB_PICK_COUNT = 3;

/** @deprecated Use NON_ELITE_QB_PICK_WINDOWS[0] — kept for action copy. */
const BALANCED_QB_PICK_RANGE = NON_ELITE_QB_PICK_WINDOWS[0];

const BEST_BALL_STARTER_SLOTS = { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 1 };

export { POSITION_COLORS };

function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value));
}

function lerp(a, b, t) {
	return a + (b - a) * t;
}

function emptyCounts() {
	return { QB: 0, RB: 0, WR: 0, TE: 0 };
}

function normalizeWeights(weights) {
	const sum = POSITIONS.reduce((acc, pos) => acc + weights[pos], 0);
	if (sum <= 0) return { QB: 0.25, RB: 0.25, WR: 0.25, TE: 0.25 };
	const normalized = {};
	POSITIONS.forEach((pos) => {
		normalized[pos] = weights[pos] / sum;
	});
	return normalized;
}

function lerpComposition(a, b, t) {
	return {
		QB: lerp(a.QB, b.QB, t),
		RB: lerp(a.RB, b.RB, t),
		WR: lerp(a.WR, b.WR, t),
		TE: lerp(a.TE, b.TE, t)
	};
}

function roundComposition(comp) {
	return {
		QB: Math.round(comp.QB),
		RB: Math.round(comp.RB),
		WR: Math.round(comp.WR),
		TE: Math.round(comp.TE)
	};
}

function applyPositionCaps(comp) {
	return {
		QB: clamp(comp.QB, MIN_QB, MAX_QB),
		RB: Math.max(0, comp.RB),
		WR: Math.max(0, comp.WR),
		TE: clamp(comp.TE, MIN_TE, MAX_TE)
	};
}

function rebalanceToTotal(comp, totalRounds) {
	let result = applyPositionCaps({ ...comp });
	let sum = POSITIONS.reduce((acc, pos) => acc + result[pos], 0);

	const skillPositions = ['RB', 'WR'];

	while (sum < totalRounds) {
		const target = skillPositions.reduce((best, pos) =>
			result[pos] <= result[best] ? pos : best
		);
		result[target] += 1;
		sum += 1;
	}

	while (sum > totalRounds) {
		if (result.WR > 4) {
			result.WR -= 1;
		} else if (result.RB > 4) {
			result.RB -= 1;
		} else if (result.QB > MIN_QB) {
			result.QB -= 1;
		} else if (result.TE > MIN_TE) {
			result.TE -= 1;
		} else {
			break;
		}
		sum -= 1;
	}

	if (sum !== totalRounds) {
		const delta = totalRounds - sum;
		result.WR = Math.max(0, result.WR + delta);
	}

	return applyPositionCaps(result);
}

function hasEliteModifier(options = {}) {
	return Boolean(options.eliteQB || options.eliteTE);
}

function getZeroWrAnchor18(options = {}) {
	return hasEliteModifier(options) ? ZERO_WR_EXTREME_18.elite : ZERO_WR_EXTREME_18.standard;
}

function getBalancedAnchor18(options = {}) {
	const eliteQB = Boolean(options.eliteQB);
	const eliteTE = Boolean(options.eliteTE);
	const lateQB = Boolean(options.lateQB);
	const lateTE = Boolean(options.lateTE);
	if (eliteQB && eliteTE) return ANCHOR_COMPOSITION_18.balancedEliteBoth;
	if (eliteTE) return ANCHOR_COMPOSITION_18.balancedEliteTE;
	if (eliteQB) return ANCHOR_COMPOSITION_18.balancedEliteQB;
	if (lateQB) return { ...ANCHOR_COMPOSITION_18.balanced, QB: LATE_QB_PICK_COUNT };
	if (lateTE) return { ...ANCHOR_COMPOSITION_18.balanced, TE: LATE_TE_PICK_COUNT };
	return ANCHOR_COMPOSITION_18.balanced;
}

function isExtremeZeroWr(spectrumValue) {
	return spectrumValue <= 15;
}

function isExtremeZeroRb(spectrumValue) {
	return spectrumValue >= 85;
}

/** Hero poles (RB0 / WR100) or Zero poles (WR0 / RB100 on engine slider): fixed TE + WR bridge scoring. */
function isSkillExtremeSchedule(spectrumValue, options = {}) {
	if (isHeroConstructionMode(options)) {
		return (
			(heroWrEmphasis(spectrumValue) > 0 && spectrumValue >= 85) ||
			(heroRbEmphasis(spectrumValue) > 0 && spectrumValue <= 15)
		);
	}
	return isExtremeZeroWr(spectrumValue) || isExtremeZeroRb(spectrumValue);
}

/** Map an 18-round anchor round onto `totalRounds` (15–20). */
function extremeScheduleRoundFrom18(round18, totalRounds) {
	return clamp(Math.round((round18 / 18) * totalRounds), 1, totalRounds);
}

function heroRbOpenCount(spectrumValue, options = {}) {
	if (isHeroConstructionMode(options)) return 0;
	if (spectrumValue >= 42) return 0;
	if (spectrumValue <= 15) return 4;
	return Math.round(lerp(4, 1, (spectrumValue - 15) / 27));
}

/** Hero WR opens in rounds 1–6 at extreme Zero RB (spectrum 100 → 6 straight WR). */
function heroWrOpenCount(spectrumValue, options = {}) {
	if (isHeroConstructionMode(options)) return 0;
	if (spectrumValue <= 58) return 0;
	if (spectrumValue >= 85) return 6;
	return Math.round(lerp(0, 6, (spectrumValue - 58) / 27));
}

function getHeroTargetComposition(spectrumValue, totalRounds, options = {}) {
	const value = clamp(spectrumValue, 0, 100);
	const balanced = getBalancedAnchor18(options);
	const extreme = { ...HERO_SKILL_EXTREME_18 };
	let comp18;

	if (value <= 15) {
		comp18 = { ...extreme };
	} else if (value <= 50) {
		const t = (value - 15) / 35;
		comp18 = lerpComposition(extreme, balanced, t);
	} else if (value >= 85) {
		comp18 = { ...extreme };
	} else {
		const t = (value - 50) / 35;
		comp18 = lerpComposition(balanced, extreme, t);
	}

	const scale = totalRounds / 18;
	const scaled = roundComposition({
		QB: comp18.QB * scale,
		RB: comp18.RB * scale,
		WR: comp18.WR * scale,
		TE: comp18.TE * scale
	});

	let result = rebalanceToTotal(scaled, totalRounds);
	if (options.lateQB) {
		result = { ...result, QB: clamp(LATE_QB_PICK_COUNT, MIN_QB, MAX_QB) };
		result = rebalanceToTotal(result, totalRounds);
	}
	if (options.lateTE) {
		result = { ...result, TE: clamp(LATE_TE_PICK_COUNT, MIN_TE, MAX_TE) };
		result = rebalanceToTotal(result, totalRounds);
	}
	return result;
}

function getTargetComposition(spectrumValue, totalRounds, options = {}) {
	if (isHeroConstructionMode(options)) {
		return getHeroTargetComposition(spectrumValue, totalRounds, options);
	}

	const value = clamp(spectrumValue, 0, 100);
	let comp18;

	if (value <= 50) {
		const zeroWrAnchor = getZeroWrAnchor18(options);
		if (value <= 15) {
			comp18 = { ...zeroWrAnchor };
		} else {
			const t = (value - 15) / 35;
			comp18 = lerpComposition(zeroWrAnchor, getBalancedAnchor18(options), t);
		}
	} else if (value >= 85) {
		comp18 = { ...ANCHOR_COMPOSITION_18.zeroRb };
	} else {
		const t = (value - 50) / 35;
		comp18 = lerpComposition(getBalancedAnchor18(options), ANCHOR_COMPOSITION_18.zeroRb, t);
	}

	const scale = totalRounds / 18;
	const scaled = roundComposition({
		QB: comp18.QB * scale,
		RB: comp18.RB * scale,
		WR: comp18.WR * scale,
		TE: comp18.TE * scale
	});

	let result = rebalanceToTotal(scaled, totalRounds);
	if (options.lateQB) {
		result = { ...result, QB: clamp(LATE_QB_PICK_COUNT, MIN_QB, MAX_QB) };
		result = rebalanceToTotal(result, totalRounds);
	}
	if (options.lateTE) {
		result = { ...result, TE: clamp(LATE_TE_PICK_COUNT, MIN_TE, MAX_TE) };
		result = rebalanceToTotal(result, totalRounds);
	}
	return result;
}

function lateTERoundWindow(totalRounds) {
	return scaledRoundRange(LATE_TE_ROUND_RANGE_18[0], LATE_TE_ROUND_RANGE_18[1], totalRounds);
}

/** Three TE slots between rounds 12–18 (18-round base), evenly spaced with a late-half bias. */
function lateTEScheduleRounds(totalRounds, teCount = LATE_TE_PICK_COUNT) {
	const window = lateTERoundWindow(totalRounds);
	const count = clamp(teCount, 1, MAX_TE);
	const biasedStart = Math.round(window.low + (window.high - window.low) * 0.25);
	const slots = spreadSlotIndices(count, biasedStart / totalRounds, window.high / totalRounds, totalRounds);
	let prev = window.low - 1;
	const out = slots.map((round, index) => {
		const minAllowed = index === 0 ? window.low : prev + 1;
		const r = clamp(round, minAllowed, window.high);
		prev = r;
		return r;
	});
	if (out.length > 0) {
		out[out.length - 1] = window.high;
		for (let i = out.length - 2; i >= 0; i -= 1) {
			out[i] = Math.min(out[i], out[i + 1] - 1);
			out[i] = clamp(out[i], window.low, window.high);
		}
	}
	return out;
}

function lateQBRoundWindow(totalRounds) {
	return scaledRoundRange(LATE_QB_ROUND_RANGE_18[0], LATE_QB_ROUND_RANGE_18[1], totalRounds);
}

/** Three QB slots between rounds 12–18 (18-round base), evenly spaced with an early-half bias. */
function lateQBScheduleRounds(totalRounds, qbCount = LATE_QB_PICK_COUNT) {
	const window = lateQBRoundWindow(totalRounds);
	const count = clamp(qbCount, 1, MAX_QB);
	const biasedEnd = Math.round(window.low + (window.high - window.low) * 0.75);
	const slots = spreadSlotIndices(
		count,
		window.low / totalRounds,
		biasedEnd / totalRounds,
		totalRounds
	);
	return slots.map((round, index) => {
		const maxAllowed = index === count - 1 ? window.high : slots[index + 1] - 1;
		return clamp(round, window.low, Math.max(window.low, maxAllowed));
	});
}

function getPhaseForRound(round, totalRounds) {
	const ratio = round / totalRounds;
	return ROUND_PHASES.find((p) => ratio <= p.maxRoundRatio) || ROUND_PHASES[ROUND_PHASES.length - 1];
}

function spreadSlotIndices(count, startRatio, endRatio, totalRounds) {
	if (count <= 0) return [];
	if (count === 1) return [Math.round(totalRounds * (startRatio + endRatio) / 2)];

	const slots = [];
	for (let i = 0; i < count; i += 1) {
		const t = i / (count - 1);
		const ratio = lerp(startRatio, endRatio, t);
		slots.push(clamp(Math.round(ratio * totalRounds), 1, totalRounds));
	}
	return slots;
}

function revisitRoundCount(totalRounds) {
	return totalRounds >= 18 ? 3 : 2;
}

function isBalancedSpectrum(spectrumValue) {
	return spectrumValue >= 38 && spectrumValue <= 62;
}

export function normalizeFormat(format) {
	return format === 'managed' ? 'managed' : 'best_ball';
}

function isBestBallFormat(format) {
	return normalizeFormat(format) === 'best_ball';
}

function shouldEmphasizeBestBallWr(spectrumValue, format, round, cumulative, roundMeta = {}) {
	if (!isBestBallFormat(format) || !isBalancedSpectrum(spectrumValue)) return false;
	if (roundMeta.isHeroRbOpen || roundMeta.isElitePick || roundMeta.isRevisitRound) return false;
	if (round > 10) return false;
	return (cumulative?.RB || 0) >= BEST_BALL_STARTER_SLOTS.RB;
}

/**
 * Execution rules tailored to the active build (spectrum, format, elites, late TE).
 */
export function buildExecutionRules(context = {}) {
	const {
		spectrumValue = 50,
		totalRounds = 18,
		format = 'best_ball',
		eliteQB = false,
		eliteTE = false,
		lateQB = false,
		lateTE = false,
		compositionLine = '',
		lateQBRounds = [],
		lateTERounds = []
	} = context;

	const rules = [];
	const constructionMode = normalizeConstructionMode(context.constructionMode);
	const heroMode = constructionMode === 'hero';
	const wrOpens = heroWrOpenCount(spectrumValue, context);
	const rbOpens = heroRbOpenCount(spectrumValue, context);
	const rbEmphasis = heroRbEmphasis(spectrumValue);
	const wrEmphasis = heroWrEmphasis(spectrumValue);
	const teWindow = nonEliteTE1RoundWindow(totalRounds);
	const lateTeWindow = lateTERoundWindow(totalRounds);

	if (compositionLine) {
		rules.push(`Target composition (${totalRounds} picks): ${compositionLine}.`);
	}

	if (heroMode && rbEmphasis > 0 && spectrumValue <= 15) {
		const pauseUntil = heroPauseUntilRound(rbEmphasis, totalRounds);
		rules.push(
			`Extreme Hero RB: RB1 in round 1 only; avoid RB until round ${pauseUntil}; target 6 RB · 7 WR on 18 picks; revisit RB in the return window.`
		);
	} else if (heroMode && wrEmphasis > 0 && spectrumValue >= 85) {
		const pauseUntil = heroPauseUntilRound(wrEmphasis, totalRounds);
		rules.push(
			`Extreme Hero WR: WR1 in round 1 only; avoid WR until round ${pauseUntil}; target 6 RB · 7 WR on 18 picks; revisit WR in the return window.`
		);
	} else if (heroMode && rbEmphasis > 0) {
		const pauseUntil = heroPauseUntilRound(rbEmphasis, totalRounds);
		rules.push(
			`Hero RB lean: anchor RB1 round 1; pause RB until round ${pauseUntil}; WR/QB/TE volume between anchor and revisit.`
		);
	} else if (heroMode && wrEmphasis > 0) {
		const pauseUntil = heroPauseUntilRound(wrEmphasis, totalRounds);
		rules.push(
			`Hero WR lean: anchor WR1 round 1; pause WR until round ${pauseUntil}; RB/QB/TE volume between anchor and revisit.`
		);
	} else if (heroMode && isBalancedSpectrum(spectrumValue)) {
		rules.push(
			'Hero mode balanced: same composition as balanced Zero mode — BPA with light positional spacing, no forced R1 hero anchor.'
		);
	} else if (spectrumValue <= 15) {
		rules.push(
			`Extreme Zero WR: open ${rbOpens >= 4 ? 'RB-RB-RB-RB' : `${rbOpens} RB`} in rounds 1–${Math.min(4, rbOpens)}; WR volume after the RB run.`
		);
	} else if (spectrumValue >= 85) {
		rules.push(
			`Extreme Zero RB: ${wrOpens} straight WR in rounds 1–${wrOpens}; RB run from round ${wrOpens + 1}; WR7 on round ${totalRounds} (final pick).`
		);
	} else if (isBalancedSpectrum(spectrumValue)) {
		if (isBestBallFormat(format)) {
			rules.push(
				`Balanced best ball: lock ${BEST_BALL_STARTER_SLOTS.RB} RBs early, prioritize WR volume rounds 3–10, then 1–2 late spike darts.`
			);
		} else {
			rules.push(
				'Balanced managed: leave rounds 1–4 with at least one RB and one WR you would start; favor stable weekly floors over spike-only profiles.'
			);
		}
	} else if (spectrumValue < 38) {
		rules.push(
			'RB-leaning build: stack RB early while tiers have workload; weave WR volume in the middle without skipping QB/TE windows.'
		);
	} else {
		rules.push(
			'WR-leaning build: attack WR target share early; start RB accumulation by the mid rounds before the tier cliff.'
		);
	}

	if (eliteTE) {
		rules.push('Elite TE: slot top-3 TE in rounds 2–4; next 2–3 rounds refocus RB/WR before more TE.');
	} else if (lateTE) {
		const roundLabel =
			lateTERounds.length > 0
				? lateTERounds.map((r) => `R${r}`).join(', ')
				: `R${lateTeWindow.low}–${lateTeWindow.high}`;
		rules.push(
			`Late TE: avoid TE before round ${lateTeWindow.low}; three TEs in ${roundLabel} (even spacing, late-half bias).`
		);
	} else {
		rules.push(
			`TE timing: first TE ~rounds ${teWindow.low}–${teWindow.high} (picks ${NON_ELITE_TE_FIRST_PICK_RANGE[0]}–${NON_ELITE_TE_FIRST_PICK_RANGE[1]}, 12-team); backups after.`
		);
	}

	if (eliteQB) {
		rules.push('Elite QB: top-4 QB in rounds 3–5; QB2/QB3 in late pick windows with stack upside.');
	} else if (lateQB) {
		const lateQbWindow = lateQBRoundWindow(totalRounds);
		const roundLabel =
			lateQBRounds.length > 0
				? lateQBRounds.map((r) => `R${r}`).join(', ')
				: `R${lateQbWindow.low}–${lateQbWindow.high}`;
		rules.push(
			`Late QB: avoid QB before round ${lateQbWindow.low}; three QBs in ${roundLabel} (even spacing, early-half bias).`
		);
	} else {
		rules.push(
			`QB timing: QB1 picks ${NON_ELITE_QB_PICK_WINDOWS[0][0]}–${NON_ELITE_QB_PICK_WINDOWS[0][1]}, QB2 ${NON_ELITE_QB_PICK_WINDOWS[1][0]}–${NON_ELITE_QB_PICK_WINDOWS[1][1]}, QB3 ${NON_ELITE_QB_PICK_WINDOWS[2][0]}–${NON_ELITE_QB_PICK_WINDOWS[2][1]} (12-team).`
		);
	}

	if (isBestBallFormat(format)) {
		rules.push(
			`Best ball starters: ${BEST_BALL_STARTER_SLOTS.QB} QB · ${BEST_BALL_STARTER_SLOTS.RB} RB · ${BEST_BALL_STARTER_SLOTS.WR} WR · ${BEST_BALL_STARTER_SLOTS.TE} TE · ${BEST_BALL_STARTER_SLOTS.FLEX} FLEX.`
		);
		if (spectrumValue > 55 || spectrumValue < 45) {
			rules.push('Use QB stacks and bring-backs when they raise weekly ceiling without breaking roster geometry.');
		}
	} else {
		rules.push(
			'Managed: same pick windows as best ball, but prioritize bye coverage and rosterable weekly floors over pure spike stacking.'
		);
	}

	rules.push('Never exceed 3 QB or 3 TE on the roster.');

	return rules;
}

function enrichMetaWithExecutionRules(baseMeta, context) {
	return {
		...baseMeta,
		rules: buildExecutionRules(context)
	};
}

function picksToRoundMid(pickLow, pickHigh, totalRounds, teamCount = DEFAULT_LEAGUE_SIZE) {
	const totalPicks = totalRounds * teamCount;
	const midPick = (pickLow + pickHigh) / 2;
	return clamp(Math.round((midPick / totalPicks) * totalRounds), 1, totalRounds);
}

function picksToRoundRange(pickLow, pickHigh, totalRounds, teamCount = DEFAULT_LEAGUE_SIZE) {
	const totalPicks = totalRounds * teamCount;
	return {
		low: clamp(Math.round((pickLow / totalPicks) * totalRounds), 1, totalRounds),
		high: clamp(Math.round((pickHigh / totalPicks) * totalRounds), 1, totalRounds)
	};
}

/** Scale round targets from an 18-round anchor (e.g. TE1 rounds 9–10). */
function scaledRoundRange(roundLowAnchor, roundHighAnchor, totalRounds, anchorRounds = 18) {
	const low = clamp(Math.round((roundLowAnchor / anchorRounds) * totalRounds), 1, totalRounds);
	const high = clamp(
		Math.round((roundHighAnchor / anchorRounds) * totalRounds),
		low,
		totalRounds
	);
	return { low, high };
}

function roundsToOverallPickRange(roundLow, roundHigh, teamCount = DEFAULT_LEAGUE_SIZE) {
	return [(roundLow - 1) * teamCount + 1, roundHigh * teamCount];
}

function nonEliteTE1RoundWindow(totalRounds) {
	const byPick = picksToRoundRange(
		NON_ELITE_TE_FIRST_PICK_RANGE[0],
		NON_ELITE_TE_FIRST_PICK_RANGE[1],
		totalRounds
	);
	const byRound = scaledRoundRange(
		NON_ELITE_TE_FIRST_ROUND_RANGE_18[0],
		NON_ELITE_TE_FIRST_ROUND_RANGE_18[1],
		totalRounds
	);
	return {
		low: Math.min(byPick.low, byRound.low),
		high: Math.max(byPick.high, byRound.high)
	};
}

function nonEliteTE1PickRange(totalRounds, teamCount = DEFAULT_LEAGUE_SIZE) {
	return [
		NON_ELITE_TE_FIRST_PICK_RANGE[0],
		NON_ELITE_TE_FIRST_PICK_RANGE[1]
	];
}

function pickRangeMidRound(pickLow, pickHigh, totalRounds, teamCount = DEFAULT_LEAGUE_SIZE) {
	const { low, high } = picksToRoundRange(pickLow, pickHigh, totalRounds, teamCount);
	return clamp(Math.round((low + high) / 2), low, high);
}

function nonEliteQBRoundByIndex(qbIndex, qbTotal, totalRounds, spectrumValue = 50) {
	if (qbIndex < NON_ELITE_QB_PICK_WINDOWS.length) {
		const [low, high] = NON_ELITE_QB_PICK_WINDOWS[qbIndex];
		let round = pickRangeMidRound(low, high, totalRounds);
		const heroWrOpens = heroWrOpenCount(spectrumValue);
		if (qbIndex === 0 && heroWrOpens > 0) {
			round = Math.max(round, heroWrOpens + 3);
		}
		return round;
	}
	const lastWindow = NON_ELITE_QB_PICK_WINDOWS[NON_ELITE_QB_PICK_WINDOWS.length - 1];
	const floor = pickRangeMidRound(lastWindow[0], lastWindow[1], totalRounds);
	const fromEnd = qbTotal - qbIndex;
	return clamp(totalRounds - fromEnd + 1, floor + 1, totalRounds);
}

function nonEliteFirstTERound(totalRounds) {
	const { low, high } = nonEliteTE1RoundWindow(totalRounds);
	return clamp(Math.round((low + high) / 2), low, high);
}

function nonEliteTERoundByIndex(teIndex, teCount, totalRounds, spectrumValue = 50, options = {}) {
	const { eliteTE = false, lateTE = false } = options;
	if (
		!eliteTE &&
		!lateTE &&
		teCount >= 2 &&
		isSkillExtremeSchedule(spectrumValue, options) &&
		teIndex <= 1
	) {
		if (teIndex === 0) return extremeScheduleRoundFrom18(9, totalRounds);
		return extremeScheduleRoundFrom18(14, totalRounds);
	}
	if (teIndex === 0) return nonEliteFirstTERound(totalRounds);
	const te1Window = nonEliteTE1RoundWindow(totalRounds);
	const anchor = te1Window.high;
	const roundsLeft = totalRounds - anchor;
	const step = Math.max(2, Math.round(roundsLeft / Math.max(teCount - teIndex + 1, 1)));
	return clamp(anchor + teIndex * step, anchor + 2, totalRounds);
}

function isLateTERoundMeta(roundMeta) {
	if (!roundMeta) return false;
	return Boolean(roundMeta.isLateTE || roundMeta.anchorType?.startsWith('LateTE'));
}

function isLateQBRoundMeta(roundMeta) {
	if (!roundMeta) return false;
	return Boolean(roundMeta.isLateQB || roundMeta.anchorType?.startsWith('LateQB'));
}

function eliteQBRound(spectrumValue, totalRounds) {
	// Elite QB window: roughly rounds 3–5 on an 18-pick draft
	const ratio = lerp(0.2, 0.24, spectrumValue / 100);
	return clamp(Math.round(totalRounds * ratio), 3, Math.max(3, Math.ceil(totalRounds * 0.32)));
}

function eliteTERound(spectrumValue, totalRounds) {
	// Elite TE tier break: rounds 2–4 on an 18-pick draft
	const ratio = lerp(0.14, 0.2, spectrumValue / 100);
	return clamp(Math.round(totalRounds * ratio), 2, Math.max(2, Math.ceil(totalRounds * 0.28)));
}

function buildRevisitSequence(spectrumValue, count) {
	if (count <= 0) return [];
	const sequence = [];
	for (let i = 0; i < count; i += 1) {
		if (spectrumValue <= 20) {
			sequence.push('WR');
		} else if (spectrumValue < 42) {
			sequence.push('RB');
		} else if (spectrumValue > 58) {
			sequence.push('WR');
		} else {
			sequence.push(i % 2 === 0 ? 'RB' : 'WR');
		}
	}
	return sequence;
}

function assignSkillPickMinRound(pos, totalRounds, options = {}) {
	const { eliteQB = false, eliteTE = false, lateQB = false, lateTE = false } = options;
	if (pos === 'TE' && !eliteTE && !lateTE) {
		return nonEliteTE1RoundWindow(totalRounds).low;
	}
	if (pos === 'QB' && !eliteQB && !lateQB) {
		const w = picksToRoundRange(
			NON_ELITE_QB_PICK_WINDOWS[0][0],
			NON_ELITE_QB_PICK_WINDOWS[0][1],
			totalRounds
		);
		return w.low;
	}
	return 1;
}

function idealPickSlots(pos, index, target, spectrumValue, totalRounds, options = {}) {
	const {
		eliteQB = false,
		eliteTE = false,
		lateQB = false,
		lateTE = false,
		skipEliteSlot = false,
		format = 'best_ball'
	} = options;
	const rbLean = 1 - spectrumValue / 100;
	const wrLean = spectrumValue / 100;
	/** Balanced best-ball starter spacing (RB/WR windows). Zero mode always uses this when balanced; Hero uses it only when there is no hero RB/WR pole emphasis (slider ~50), so Hero balanced + Elite QB matches Zero balanced scheduling. */
	const heroBalancedNoPole =
		isHeroConstructionMode(options) &&
		heroRbEmphasis(spectrumValue) === 0 &&
		heroWrEmphasis(spectrumValue) === 0;
	const bestBallBalanced =
		isBestBallFormat(format) &&
		isBalancedSpectrum(spectrumValue) &&
		heroRbOpenCount(spectrumValue, options) === 0 &&
		(!isHeroConstructionMode(options) || heroBalancedNoPole);

	switch (pos) {
		case 'QB': {
			if (lateQB && !skipEliteSlot) {
				const lateSlots = lateQBScheduleRounds(totalRounds, target.QB);
				return lateSlots[index] || lateSlots[lateSlots.length - 1];
			}
			if (eliteQB && index === 0 && !skipEliteSlot) {
				return eliteQBRound(spectrumValue, totalRounds);
			}
			const qbIndex = eliteQB && !skipEliteSlot ? index - 1 : index;
			const qbRemaining = eliteQB && !skipEliteSlot ? target.QB - 1 : target.QB;

			if (!eliteQB && !lateQB) {
				return nonEliteQBRoundByIndex(qbIndex, qbRemaining, totalRounds, spectrumValue);
			}

			const fromEnd = qbRemaining - qbIndex;
			return clamp(Math.round(totalRounds - fromEnd * 1.4), Math.ceil(totalRounds * 0.58), totalRounds);
		}
		case 'TE': {
			if (lateTE && !skipEliteSlot) {
				const lateSlots = lateTEScheduleRounds(totalRounds, target.TE);
				return lateSlots[index] || lateSlots[lateSlots.length - 1];
			}
			if (eliteTE && index === 0 && !skipEliteSlot) {
				return eliteTERound(spectrumValue, totalRounds);
			}
			const teIndex = eliteTE && !skipEliteSlot ? index - 1 : index;
			const teCount = eliteTE && !skipEliteSlot ? target.TE - 1 : target.TE;

			if (!eliteTE && !lateTE) {
				return nonEliteTERoundByIndex(teIndex, teCount, totalRounds, spectrumValue, options);
			}

			if (teIndex === 0) {
				return clamp(Math.round(totalRounds * lerp(0.38, 0.28, rbLean)), 3, totalRounds);
			}
			return clamp(
				Math.round(totalRounds * lerp(0.72, 0.82, teIndex / Math.max(teCount - 1, 1))),
				Math.ceil(totalRounds * 0.55),
				totalRounds
			);
		}
		case 'RB': {
			if (isHeroConstructionMode(options)) {
				const wrEmphasis = heroWrEmphasis(spectrumValue);
				if (wrEmphasis > 0) {
					const pauseUntil = heroPauseUntilRound(wrEmphasis, totalRounds);
					const stack = Math.min(4, target.RB);
					if (index < stack) {
						return index + 2;
					}
					const lateRb = Math.max(1, target.RB - stack);
					const slots = spreadSlotIndices(lateRb, pauseUntil / totalRounds, 0.92, totalRounds);
					return slots[index - stack] || slots[slots.length - 1] || pauseUntil;
				}
				const rbEmphasis = heroRbEmphasis(spectrumValue);
				if (rbEmphasis > 0) {
					const pauseUntil = heroPauseUntilRound(rbEmphasis, totalRounds);
					const rev = revisitRoundCount(totalRounds);
					if (index === 0) return 1;
					if (index === 1) {
						return clamp(pauseUntil - 3, 2, Math.max(2, pauseUntil - 1));
					}
					const tail = Math.max(1, target.RB - 2 - rev);
					const slots = spreadSlotIndices(tail, pauseUntil / totalRounds, 0.92, totalRounds);
					return slots[index - 2] || slots[slots.length - 1] || pauseUntil;
				}
			}
			const heroWrOpens = heroWrOpenCount(spectrumValue, options);
			if (heroWrOpens > 0) {
				const firstRbRound = heroWrOpens + 1;
				const rbSlots = spreadSlotIndices(
					target.RB,
					firstRbRound / totalRounds,
					0.9,
					totalRounds
				);
				return rbSlots[index] || clamp(firstRbRound + index, firstRbRound, totalRounds);
			}
			const heroOpens = heroRbOpenCount(spectrumValue, options);
			if (bestBallBalanced && index < BEST_BALL_STARTER_SLOTS.RB) {
				const starterRbSlots = spreadSlotIndices(
					BEST_BALL_STARTER_SLOTS.RB,
					0.06,
					lerp(0.18, 0.22, wrLean),
					totalRounds
				);
				return starterRbSlots[index];
			}
			const rbIndex = index + Math.min(heroOpens, 4);
			const rbStart = bestBallBalanced ? 0.22 : lerp(0.06, 0.2, 1 - rbLean);
			const rbEnd = bestBallBalanced ? 0.72 : lerp(0.55, 0.38, rbLean);
			const rbSlots = spreadSlotIndices(target.RB, rbStart, rbEnd, totalRounds);
			return rbSlots[rbIndex] || rbSlots[index] || Math.round(totalRounds * 0.35);
		}
		case 'WR': {
			if (isHeroConstructionMode(options)) {
				const rbEmphasis = heroRbEmphasis(spectrumValue);
				const wrEmphasis = heroWrEmphasis(spectrumValue);
				if (rbEmphasis > 0 && wrEmphasis === 0) {
					const pauseUntil = heroPauseUntilRound(rbEmphasis, totalRounds);
					const stack = Math.min(4, target.WR);
					if (index < stack) {
						return index + 2;
					}
					if (isSkillExtremeSchedule(spectrumValue, options) && index === stack) {
						return extremeScheduleRoundFrom18(7, totalRounds);
					}
					const bridge = isSkillExtremeSchedule(spectrumValue, options) ? 1 : 0;
					const lateWr = Math.max(1, target.WR - stack - bridge);
					const lateIdx = index - stack - bridge;
					const slots = spreadSlotIndices(lateWr, pauseUntil / totalRounds, 0.92, totalRounds);
					return slots[lateIdx] || slots[slots.length - 1] || pauseUntil;
				}
				if (wrEmphasis > 0) {
					const pauseUntil = heroPauseUntilRound(wrEmphasis, totalRounds);
					const rev = revisitRoundCount(totalRounds);
					if (index === 0) return 1;
					if (index === 1) {
						if (isSkillExtremeSchedule(spectrumValue, options)) {
							return extremeScheduleRoundFrom18(7, totalRounds);
						}
						return clamp(pauseUntil - 3, 2, Math.max(2, pauseUntil - 1));
					}
					const tail = Math.max(1, target.WR - 2 - rev);
					const slots = spreadSlotIndices(tail, pauseUntil / totalRounds, 0.92, totalRounds);
					return slots[index - 2] || slots[slots.length - 1] || pauseUntil;
				}
			}
			const heroWrOpens = heroWrOpenCount(spectrumValue, options);
			if (heroWrOpens > 0 && index < heroWrOpens) {
				return index + 1;
			}
			if (heroWrOpens > 0) {
				const ext = isSkillExtremeSchedule(spectrumValue, options);
				if (ext && index === heroWrOpens) {
					// Single “late” WR to complete the room: keep it on the final pick so it
					// does not collide with the RB run starting round heroWrOpens + 1 (was ~R7
					// via extremeScheduleRoundFrom18 and got bumped mid-draft).
					if (isExtremeZeroRb(spectrumValue)) {
						return totalRounds;
					}
					return extremeScheduleRoundFrom18(7, totalRounds);
				}
				if (index > heroWrOpens || (!ext && index === heroWrOpens)) {
					const skip = ext ? 1 : 0;
					const lateCount = Math.max(1, target.WR - heroWrOpens - skip);
					const lateIndex = index - heroWrOpens - (ext ? 1 : 0);
					const lateSlots = spreadSlotIndices(lateCount, 0.78, 0.98, totalRounds);
					return lateSlots[lateIndex] || totalRounds - lateCount + lateIndex + 1;
				}
			}
			if (!isHeroConstructionMode(options) && isSkillExtremeSchedule(spectrumValue, options) && isExtremeZeroWr(spectrumValue)) {
				if (index === 0) return extremeScheduleRoundFrom18(7, totalRounds);
				const rest = Math.max(1, target.WR - 1);
				const slots = spreadSlotIndices(rest, 8 / totalRounds, 0.95, totalRounds);
				return slots[index - 1] || slots[slots.length - 1] || totalRounds;
			}
			const wrStart = bestBallBalanced ? 0.12 : lerp(0.1, 0.06, wrLean);
			const wrEnd = bestBallBalanced ? 0.56 : lerp(0.88, 0.72, wrLean);
			const wrSlots = spreadSlotIndices(target.WR, wrStart, wrEnd, totalRounds);
			return wrSlots[index] || Math.round(totalRounds * 0.5);
		}
		default:
			return totalRounds;
	}
}

function assignPickToSchedule(schedule, round, pos, minRound = 1) {
	const maxRound = schedule.length;
	const mr = clamp(minRound, 1, maxRound);
	let slot = clamp(round, mr, maxRound);

	for (let r = slot; r <= maxRound; r += 1) {
		if (!schedule[r - 1]) {
			slot = r;
			schedule[slot - 1] = pos;
			return slot;
		}
	}
	for (let r = slot - 1; r >= mr; r -= 1) {
		if (!schedule[r - 1]) {
			slot = r;
			schedule[slot - 1] = pos;
			return slot;
		}
	}
	for (let r = mr; r <= maxRound; r += 1) {
		if (!schedule[r - 1]) {
			schedule[r - 1] = pos;
			return r;
		}
	}
	for (let r = 1; r <= maxRound; r += 1) {
		if (!schedule[r - 1]) {
			schedule[r - 1] = pos;
			return r;
		}
	}
	return clamp(round, 1, maxRound);
}

function placeAnchorInWindow(
	schedule,
	roundMeta,
	reservedRounds,
	targetRound,
	windowLow,
	windowHigh,
	pos,
	meta
) {
	const low = clamp(windowLow, 1, schedule.length);
	const high = clamp(windowHigh, low, schedule.length);
	const center = clamp(targetRound, low, high);
	const candidates = [];

	for (let r = center; r >= low; r -= 1) candidates.push(r);
	for (let r = center + 1; r <= high; r += 1) candidates.push(r);

	let slot = candidates.find((r) => !schedule[r - 1]);
	if (!slot) slot = assignPickToSchedule(schedule, center, pos, low);
	else schedule[slot - 1] = pos;

	roundMeta[slot - 1] = { ...(roundMeta[slot - 1] || {}), ...meta, anchorRound: slot };
	reservedRounds.add(slot);
	return slot;
}

function reserveNonEliteAnchorWindows(
	target,
	totalRounds,
	options,
	schedule,
	roundMeta,
	reservedRounds,
	spectrumValue = 50
) {
	const { eliteQB, eliteTE, lateQB, lateTE } = options;
	const anchors = [];

	if (!eliteTE && !lateTE && target.TE >= 1) {
		const teRounds = nonEliteTE1RoundWindow(totalRounds);
		let teTargetRound = nonEliteFirstTERound(totalRounds);
		if (isSkillExtremeSchedule(spectrumValue, options)) {
			teTargetRound = extremeScheduleRoundFrom18(9, totalRounds);
		} else if (isHeroConstructionMode(options)) {
			const wrE = heroWrEmphasis(spectrumValue);
			const rbE = heroRbEmphasis(spectrumValue);
			if (wrE > 0 || rbE > 0) {
				const pauseUntil = heroPauseUntilRound(wrE > 0 ? wrE : rbE, totalRounds);
				// Bias TE1 toward the end of the hero gap, but never before the normal non-elite
				// TE1 window — otherwise clamp(low, min(high, ideal)) collapses to `ideal` when
				// ideal < low (e.g. TE in round 3–4 vs Zero balanced ~9).
				const ideal = Math.max(pauseUntil, teRounds.low);
				teTargetRound = clamp(ideal, teRounds.low, teRounds.high);
			}
		}
		anchors.push({
			pos: 'TE',
			round: teTargetRound,
			windowLow: teRounds.low,
			windowHigh: teRounds.high,
			meta: { isAnchorWindow: true, anchorType: 'TE1' }
		});

		if (target.TE >= 2 && isSkillExtremeSchedule(spectrumValue, options)) {
			const r2 = extremeScheduleRoundFrom18(14, totalRounds);
			const wLow = Math.max(teTargetRound + 1, extremeScheduleRoundFrom18(12, totalRounds));
			const wHigh = totalRounds;
			anchors.push({
				pos: 'TE',
				round: r2,
				windowLow: clamp(wLow, 1, totalRounds),
				windowHigh: clamp(wHigh, wLow, totalRounds),
				meta: { isAnchorWindow: true, anchorType: 'TE2' }
			});
		}
	}

	if (!eliteQB && !lateQB && target.QB >= 1) {
		for (let i = 0; i < target.QB; i += 1) {
			const [pickLow, pickHigh] =
				NON_ELITE_QB_PICK_WINDOWS[i] ||
				NON_ELITE_QB_PICK_WINDOWS[NON_ELITE_QB_PICK_WINDOWS.length - 1];
			const window = picksToRoundRange(pickLow, pickHigh, totalRounds);
			anchors.push({
				pos: 'QB',
				round: nonEliteQBRoundByIndex(i, target.QB, totalRounds, spectrumValue),
				windowLow: window.low,
				windowHigh: window.high,
				meta: { isAnchorWindow: true, anchorType: `QB${i + 1}` }
			});
		}
	}

	anchors.sort((a, b) => a.round - b.round || POSITIONS.indexOf(a.pos) - POSITIONS.indexOf(b.pos));

	anchors.forEach((anchor) => {
		placeAnchorInWindow(
			schedule,
			roundMeta,
			reservedRounds,
			anchor.round,
			anchor.windowLow,
			anchor.windowHigh,
			anchor.pos,
			anchor.meta
		);
	});
}

function reserveLateTEWindows(target, totalRounds, options, schedule, roundMeta, reservedRounds) {
	if (!options.lateTE) return [];

	const window = lateTERoundWindow(totalRounds);
	const teCount = clamp(target.TE, MIN_TE, MAX_TE);
	const targetRounds = lateTEScheduleRounds(totalRounds, teCount);
	const placedRounds = [];
	targetRounds.forEach((round, index) => {
		const slot = placeAnchorInWindow(
			schedule,
			roundMeta,
			reservedRounds,
			round,
			window.low,
			window.high,
			'TE',
			{
				isAnchorWindow: true,
				isLateTE: true,
				anchorType: `LateTE${index + 1}`
			}
		);
		placedRounds.push(slot);
	});

	return placedRounds;
}

function reserveLateQBWindows(target, totalRounds, options, schedule, roundMeta, reservedRounds) {
	if (!options.lateQB) return [];

	const window = lateQBRoundWindow(totalRounds);
	const qbCount = clamp(target.QB, MIN_QB, MAX_QB);
	const rounds = lateQBScheduleRounds(totalRounds, qbCount);

	rounds.forEach((round, index) => {
		placeAnchorInWindow(
			schedule,
			roundMeta,
			reservedRounds,
			round,
			window.low,
			window.high,
			'QB',
			{
				isAnchorWindow: true,
				isLateQB: true,
				anchorType: `LateQB${index + 1}`
			}
		);
	});

	return rounds;
}

function isEliteAlreadyPlaced(roundMeta, type) {
	return roundMeta.some((meta) => meta?.isElitePick && meta.eliteType === type);
}

function reserveHeroRbWindow(spectrumValue, totalRounds, options, placeReserved, reservedRounds) {
	const opens = heroRbOpenCount(spectrumValue);
	if (opens <= 0) return;

	const windowEnd = Math.min(4, opens);
	const blocked = new Set();

	if (options.eliteTE && spectrumValue <= 28) {
		const teRound = clamp(eliteTERound(spectrumValue, totalRounds), 2, windowEnd);
		placeReserved(teRound, 'TE', { isElitePick: true, eliteType: 'TE', isHeroWindow: true });
		blocked.add(teRound);
	}

	if (options.eliteQB && !options.lateQB && spectrumValue <= 28) {
		let qbRound = clamp(eliteQBRound(spectrumValue, totalRounds), 2, windowEnd + 1);
		while (blocked.has(qbRound) && qbRound <= windowEnd) qbRound += 1;
		if (qbRound <= windowEnd) {
			placeReserved(qbRound, 'QB', { isElitePick: true, eliteType: 'QB', isHeroWindow: true });
			blocked.add(qbRound);
		}
	}

	let rbPlaced = 0;
	for (let round = 1; round <= windowEnd && rbPlaced < opens; round += 1) {
		if (blocked.has(round)) continue;
		placeReserved(round, 'RB', { isHeroRbOpen: true });
		blocked.add(round);
		rbPlaced += 1;
	}
}

function reserveHeroWrWindow(spectrumValue, totalRounds, options, placeReserved, reservedRounds) {
	const opens = heroWrOpenCount(spectrumValue);
	if (opens <= 0) return;

	const windowEnd = Math.min(6, opens);
	const blocked = new Set();

	if (options.eliteTE && spectrumValue >= 72) {
		const teRound = clamp(eliteTERound(spectrumValue, totalRounds), 2, windowEnd);
		placeReserved(teRound, 'TE', { isElitePick: true, eliteType: 'TE', isHeroWindow: true });
		blocked.add(teRound);
	}

	if (options.eliteQB && !options.lateQB && spectrumValue >= 72) {
		let qbRound = clamp(eliteQBRound(spectrumValue, totalRounds), 2, windowEnd + 1);
		while (blocked.has(qbRound) && qbRound <= windowEnd) qbRound += 1;
		if (qbRound <= windowEnd) {
			placeReserved(qbRound, 'QB', { isElitePick: true, eliteType: 'QB', isHeroWindow: true });
			blocked.add(qbRound);
		}
	}

	let wrPlaced = 0;
	for (let round = 1; round <= windowEnd && wrPlaced < opens; round += 1) {
		if (blocked.has(round)) continue;
		placeReserved(round, 'WR', { isHeroWrOpen: true });
		blocked.add(round);
		wrPlaced += 1;
	}
}

function reserveHeroAnchorWindows(spectrumValue, totalRounds, options, placeReserved, reservedRounds, target = {}) {
	const rbStrength = heroRbEmphasis(spectrumValue);
	const wrStrength = heroWrEmphasis(spectrumValue);
	const revisitLen = revisitRoundCount(totalRounds);
	const heroRevisitRounds = [];

	const heroReserveNonEliteTeSlot =
		isHeroConstructionMode(options) &&
		!options.eliteTE &&
		!options.lateTE &&
		(target.TE ?? 0) >= 1;

	if (rbStrength > 0) {
		const pauseUntil = heroPauseUntilRound(rbStrength, totalRounds);
		const blocked = new Set();
		let revisitStart = pauseUntil;
		if (heroReserveNonEliteTeSlot) {
			const teRounds = nonEliteTE1RoundWindow(totalRounds);
			if (
				pauseUntil >= teRounds.low &&
				pauseUntil <= teRounds.high &&
				pauseUntil < totalRounds &&
				revisitStart + revisitLen - 1 <= totalRounds
			) {
				revisitStart = pauseUntil + 1;
			}
		}

		if (options.eliteTE && spectrumValue <= 28) {
			const teRound = clamp(eliteTERound(spectrumValue, totalRounds), 2, 4);
			placeReserved(teRound, 'TE', { isElitePick: true, eliteType: 'TE', isHeroWindow: true });
			blocked.add(teRound);
		}

		if (options.eliteQB && !options.lateQB && spectrumValue <= 28) {
			let qbRound = clamp(eliteQBRound(spectrumValue, totalRounds), 2, 5);
			while (blocked.has(qbRound) && qbRound <= 4) qbRound += 1;
			if (qbRound <= 4) {
				placeReserved(qbRound, 'QB', { isElitePick: true, eliteType: 'QB', isHeroWindow: true });
				blocked.add(qbRound);
			}
		}

		if (!blocked.has(1)) {
			placeReserved(1, 'RB', {
				isHeroRbAnchor: true,
				isHeroAnchor: true,
				heroAnchorPos: 'RB',
				heroPauseUntilRound: pauseUntil
			});
			blocked.add(1);
		}

		let revisitPlaced = 0;
		for (let round = revisitStart; round <= totalRounds && revisitPlaced < revisitLen; round += 1) {
			if (reservedRounds.has(round) || blocked.has(round)) continue;
			placeReserved(round, 'RB', {
				isHeroRbRevisit: true,
				isHeroRevisit: true,
				heroRevisitPos: 'RB',
				revisitAfterHero: 'RB',
				revisitAfterRound: 1,
				heroPauseUntilRound: pauseUntil
			});
			blocked.add(round);
			heroRevisitRounds.push(round);
			revisitPlaced += 1;
		}
	}

	if (wrStrength > 0) {
		const pauseUntil = heroPauseUntilRound(wrStrength, totalRounds);
		const blocked = new Set();
		let revisitStart = pauseUntil;
		if (heroReserveNonEliteTeSlot) {
			const teRounds = nonEliteTE1RoundWindow(totalRounds);
			if (
				pauseUntil >= teRounds.low &&
				pauseUntil <= teRounds.high &&
				pauseUntil < totalRounds &&
				revisitStart + revisitLen - 1 <= totalRounds
			) {
				revisitStart = pauseUntil + 1;
			}
		}

		if (options.eliteTE && spectrumValue >= 72) {
			const teRound = clamp(eliteTERound(spectrumValue, totalRounds), 2, 6);
			placeReserved(teRound, 'TE', { isElitePick: true, eliteType: 'TE', isHeroWindow: true });
			blocked.add(teRound);
		}

		if (options.eliteQB && !options.lateQB && spectrumValue >= 72) {
			let qbRound = clamp(eliteQBRound(spectrumValue, totalRounds), 2, 7);
			while (blocked.has(qbRound) && qbRound <= 6) qbRound += 1;
			if (qbRound <= 6) {
				placeReserved(qbRound, 'QB', { isElitePick: true, eliteType: 'QB', isHeroWindow: true });
				blocked.add(qbRound);
			}
		}

		if (!blocked.has(1)) {
			placeReserved(1, 'WR', {
				isHeroWrAnchor: true,
				isHeroAnchor: true,
				heroAnchorPos: 'WR',
				heroPauseUntilRound: pauseUntil
			});
			blocked.add(1);
		}

		let revisitPlaced = 0;
		for (let round = revisitStart; round <= totalRounds && revisitPlaced < revisitLen; round += 1) {
			if (reservedRounds.has(round) || blocked.has(round)) continue;
			placeReserved(round, 'WR', {
				isHeroWrRevisit: true,
				isHeroRevisit: true,
				heroRevisitPos: 'WR',
				revisitAfterHero: 'WR',
				revisitAfterRound: 1,
				heroPauseUntilRound: pauseUntil
			});
			blocked.add(round);
			heroRevisitRounds.push(round);
			revisitPlaced += 1;
		}
	}

	return heroRevisitRounds;
}

function markHeroGapMeta(roundMeta, spectrumValue, totalRounds, reservedRounds) {
	const rbStrength = heroRbEmphasis(spectrumValue);
	if (rbStrength > 0) {
		const pauseUntil = heroPauseUntilRound(rbStrength, totalRounds);
		for (let round = 2; round < pauseUntil; round += 1) {
			if (reservedRounds.has(round)) continue;
			const meta = roundMeta[round - 1] || {};
			if (meta.isHeroRbAnchor || meta.isElitePick) continue;
			roundMeta[round - 1] = {
				...meta,
				heroRbPaused: true,
				heroPauseUntilRound: pauseUntil,
				heroPausedPosition: 'RB'
			};
		}
	}
	const wrStrength = heroWrEmphasis(spectrumValue);
	if (wrStrength > 0) {
		const pauseUntil = heroPauseUntilRound(wrStrength, totalRounds);
		for (let round = 2; round < pauseUntil; round += 1) {
			if (reservedRounds.has(round)) continue;
			const meta = roundMeta[round - 1] || {};
			if (meta.isHeroWrAnchor || meta.isElitePick) continue;
			roundMeta[round - 1] = {
				...meta,
				heroWrPaused: true,
				heroPauseUntilRound: pauseUntil,
				heroPausedPosition: 'WR'
			};
		}
	}
}

/** Assign each pick to a round; guarantees final counts match target composition. */
function buildPickSchedule(target, spectrumValue, totalRounds, options = {}) {
	const { eliteQB = false, eliteTE = false, lateQB = false, lateTE = false, format = 'best_ball' } = options;
	const schedule = Array(totalRounds).fill(null);
	const roundMeta = Array(totalRounds).fill(null);
	const revisitLen = revisitRoundCount(totalRounds);
	const reservedRounds = new Set();

	const placeReserved = (round, pos, meta) => {
		const slot = assignPickToSchedule(schedule, round, pos);
		roundMeta[slot - 1] = { ...meta, eliteRound: slot };
		reservedRounds.add(slot);
		return slot;
	};

	const eliteAnchors = [];
	let heroRevisitRounds = [];

	if (isHeroConstructionMode(options)) {
		heroRevisitRounds = reserveHeroAnchorWindows(
			spectrumValue,
			totalRounds,
			options,
			placeReserved,
			reservedRounds,
			target
		);
	} else {
		reserveHeroRbWindow(spectrumValue, totalRounds, options, placeReserved, reservedRounds);
		reserveHeroWrWindow(spectrumValue, totalRounds, options, placeReserved, reservedRounds);
	}

	if (eliteQB && !lateQB && !isEliteAlreadyPlaced(roundMeta, 'QB')) {
		const targetRound = eliteQBRound(spectrumValue, totalRounds);
		placeReserved(targetRound, 'QB', { isElitePick: true, eliteType: 'QB' });
	}

	if (eliteTE && !lateTE && !isEliteAlreadyPlaced(roundMeta, 'TE')) {
		let targetRound = eliteTERound(spectrumValue, totalRounds);
		while (reservedRounds.has(targetRound) && targetRound < totalRounds) targetRound += 1;
		placeReserved(targetRound, 'TE', { isElitePick: true, eliteType: 'TE' });
	}

	reserveNonEliteAnchorWindows(
		target,
		totalRounds,
		options,
		schedule,
		roundMeta,
		reservedRounds,
		spectrumValue
	);

	const lateTERounds = reserveLateTEWindows(
		target,
		totalRounds,
		options,
		schedule,
		roundMeta,
		reservedRounds
	);

	const lateQBRounds = reserveLateQBWindows(
		target,
		totalRounds,
		options,
		schedule,
		roundMeta,
		reservedRounds
	);

	roundMeta.forEach((meta, index) => {
		if (meta?.isElitePick) {
			eliteAnchors.push({ round: index + 1, pos: meta.eliteType });
		}
	});
	eliteAnchors.sort((a, b) => a.round - b.round);

	const revisitRoundList = [];
	eliteAnchors.forEach((anchor) => {
		for (let r = anchor.round + 1; r <= anchor.round + revisitLen && r <= totalRounds; r += 1) {
			if (!reservedRounds.has(r) && !revisitRoundList.includes(r)) {
				revisitRoundList.push(r);
			}
		}
	});

	const revisitPositions = buildRevisitSequence(spectrumValue, revisitRoundList.length);
	revisitRoundList.sort((a, b) => a - b).forEach((round, index) => {
		const focus = revisitPositions[index];
		const anchor = eliteAnchors.find(
			(a) => round > a.round && round <= a.round + revisitLen
		);
		placeReserved(round, focus, {
			isRevisitRound: true,
			revisitFocus: focus,
			revisitAfterElite: anchor?.pos,
			revisitAfterRound: anchor?.round
		});
	});

	const remaining = { ...target };
	roundMeta.forEach((meta, index) => {
		const pos = schedule[index];
		if (meta && pos && POSITIONS.includes(pos)) {
			remaining[pos] -= 1;
		}
	});

	const picks = [];
	const teAnchoredCount = roundMeta.reduce(
		(n, m) => n + (m?.anchorType === 'TE1' || m?.anchorType === 'TE2' ? 1 : 0),
		0
	);
	const lateTEAnchored = roundMeta.some((meta) => isLateTERoundMeta(meta));
	const lateQBAnchored = roundMeta.some((meta) => isLateQBRoundMeta(meta));
	const heroWrPlaced = roundMeta.filter((meta) => meta?.isHeroWrOpen).length;
	const heroWrAnchored = roundMeta.some((meta) => isHeroWrAnchorMeta(meta));
	const heroRbAnchored = roundMeta.some((meta) => isHeroRbAnchorMeta(meta));
	const wrHeavy = spectrumValue > 58;

	POSITIONS.forEach((pos) => {
		if (pos === 'TE' && lateTEAnchored) return;
		if (pos === 'QB' && lateQBAnchored) return;

		for (let i = 0; i < remaining[pos]; i += 1) {
			let compIndex = i;
			if (pos === 'TE' && teAnchoredCount > 0) compIndex = i + teAnchoredCount;
			if (pos === 'WR' && heroWrPlaced > 0) compIndex = heroWrPlaced + i;
			if (pos === 'WR' && heroWrAnchored) compIndex = i + 1;
			if (pos === 'RB' && heroRbAnchored) compIndex = i + 1;
			picks.push({
				pos,
				slot: idealPickSlots(pos, compIndex, remaining, spectrumValue, totalRounds, {
					...options,
					format,
					skipEliteSlot: true
				})
			});
		}
	});

	const skillPickOrder = wrHeavy
		? { RB: 0, WR: 1, QB: 2, TE: 3 }
		: { RB: 0, WR: 1, QB: 2, TE: 3 };
	picks.sort(
		(a, b) =>
			a.slot - b.slot ||
			(skillPickOrder[a.pos] ?? 9) - (skillPickOrder[b.pos] ?? 9)
	);

	picks.forEach((pick) => {
		const minRound = assignSkillPickMinRound(pick.pos, totalRounds, options);
		const slot = assignPickToSchedule(schedule, pick.slot, pick.pos, minRound);
		if (!roundMeta[slot - 1]) {
			roundMeta[slot - 1] = {};
		}
	});

	if (isHeroConstructionMode(options)) {
		markHeroGapMeta(roundMeta, spectrumValue, totalRounds, reservedRounds);
	}

	for (let i = 0; i < totalRounds; i += 1) {
		if (!schedule[i]) schedule[i] = 'WR';
		if (!roundMeta[i]) roundMeta[i] = {};
	}

	const rbEm = heroRbEmphasis(spectrumValue);
	const wrEm = heroWrEmphasis(spectrumValue);
	const heroPauseUntil =
		rbEm > 0 ? heroPauseUntilRound(rbEm, totalRounds) : wrEm > 0 ? heroPauseUntilRound(wrEm, totalRounds) : null;

	return {
		schedule: schedule.slice(0, totalRounds),
		roundMeta: roundMeta.slice(0, totalRounds),
		eliteAnchors,
		revisitRoundList,
		lateTERounds,
		lateQBRounds,
		heroRevisitRounds,
		heroPauseUntil
	};
}

function weightsForScheduledPick(
	primary,
	round,
	totalRounds,
	spectrumValue,
	roundMeta = {},
	context = {}
) {
	const {
		format = 'best_ball',
		cumulativeBefore = emptyCounts(),
		lateQB = false,
		lateTE = false,
		constructionMode = 'zero'
	} = context;
	const heroMode = constructionMode === 'hero';
	const phase = getPhaseForRound(round, totalRounds).phase;
	const weights = { QB: 0.06, RB: 0.18, WR: 0.18, TE: 0.06 };
	weights[primary] = 0.52;

	if (roundMeta.isHeroRbOpen) {
		weights.RB = 0.78;
		weights.WR = 0.1;
		weights.QB = 0.06;
		weights.TE = 0.06;
		return normalizeWeights(weights);
	}

	if (roundMeta.isHeroWrOpen) {
		weights.WR = 0.78;
		weights.RB = 0.1;
		weights.QB = 0.06;
		weights.TE = 0.06;
		return normalizeWeights(weights);
	}

	if (roundMeta.isAnchorWindow) {
		weights[primary] = 0.68;
		if (roundMeta.anchorType === 'TE1') {
			weights.WR = 0.14;
			weights.RB = 0.1;
		}
		if (roundMeta.anchorType === 'TE2') {
			weights.WR = 0.12;
			weights.RB = 0.1;
		}
		if (isLateTERoundMeta(roundMeta)) {
			weights.TE = 0.68;
			weights.WR = 0.12;
			weights.RB = 0.1;
		}
		if (isLateQBRoundMeta(roundMeta)) {
			weights.QB = 0.68;
			weights.WR = 0.12;
			weights.RB = 0.1;
		}
		if (isHeroRbRevisitMeta(roundMeta)) {
			weights.RB = 0.68;
			weights.WR = 0.12;
			weights.TE = 0.06;
		}
		if (isHeroWrRevisitMeta(roundMeta)) {
			weights.WR = 0.68;
			weights.RB = 0.12;
			weights.QB = 0.06;
		}
		if (roundMeta.anchorType?.startsWith('QB')) {
			weights.WR = 0.12;
			weights.RB = 0.1;
			weights.TE = 0.06;
		}
		return normalizeWeights(weights);
	}

	if (roundMeta.isElitePick) {
		weights[primary] = 0.72;
		weights.RB = primary === 'QB' || primary === 'TE' ? 0.12 : weights.RB;
		weights.WR = primary === 'QB' || primary === 'TE' ? 0.1 : weights.WR;
		return normalizeWeights(weights);
	}

	if (roundMeta.isRevisitRound) {
		weights[primary] = 0.62;
		weights[roundMeta.revisitAfterElite] = 0.04;
		return normalizeWeights(weights);
	}

	const wrEmphasis = shouldEmphasizeBestBallWr(
		spectrumValue,
		format,
		round,
		cumulativeBefore,
		roundMeta
	);

	if (wrEmphasis) {
		if (primary === 'WR') {
			weights.WR = 0.62;
			weights.RB = 0.14;
			weights.TE = 0.08;
			weights.QB = 0.06;
		} else if (primary === 'RB' && cumulativeBefore.RB >= BEST_BALL_STARTER_SLOTS.RB) {
			weights.WR = 0.28;
			weights.RB = 0.42;
		} else {
			weights.WR = Math.max(weights.WR, 0.26);
		}
	}

	if (isBestBallFormat(format) && phase === 'late' && round >= totalRounds - 3) {
		weights.WR = Math.max(weights.WR, 0.22);
		weights.QB = Math.max(weights.QB, 0.1);
	}

	if (primary === 'RB' && spectrumValue < 45) weights.WR = Math.max(weights.WR, 0.14);
	if (primary === 'WR' && spectrumValue > 55) weights.RB = Math.max(weights.RB, 0.14);
	if (
		lateTE &&
		!isLateTERoundMeta(roundMeta) &&
		round < lateTERoundWindow(totalRounds).low
	) {
		weights.WR = Math.max(weights.WR, 0.2);
		weights.RB = Math.max(weights.RB, 0.18);
		weights.TE = Math.min(weights.TE, 0.06);
	} else if (
		lateQB &&
		!isLateQBRoundMeta(roundMeta) &&
		round < lateQBRoundWindow(totalRounds).low
	) {
		weights.WR = Math.max(weights.WR, 0.2);
		weights.RB = Math.max(weights.RB, 0.18);
		weights.QB = Math.min(weights.QB, 0.06);
	} else if (
		heroMode &&
		roundMeta.heroRbPaused &&
		!isHeroRbAnchorMeta(roundMeta) &&
		!isHeroRbRevisitMeta(roundMeta)
	) {
		weights.WR = Math.max(weights.WR, 0.22);
		weights.RB = Math.max(weights.RB, 0.18);
		weights.QB = Math.max(weights.QB, 0.1);
		weights.RB = Math.min(weights.RB, 0.06);
	} else if (
		heroMode &&
		roundMeta.heroWrPaused &&
		!isHeroWrAnchorMeta(roundMeta) &&
		!isHeroWrRevisitMeta(roundMeta)
	) {
		weights.WR = Math.max(weights.WR, 0.18);
		weights.RB = Math.max(weights.RB, 0.22);
		weights.QB = Math.max(weights.QB, 0.1);
		weights.WR = Math.min(weights.WR, 0.06);
	} else if (primary === 'TE') {
		const te1Rounds = nonEliteTE1RoundWindow(totalRounds);
		if (!roundMeta.isElitePick && round < te1Rounds.low) {
			weights.WR = Math.max(weights.WR, 0.22);
			weights.RB = Math.max(weights.RB, 0.16);
			weights.TE = Math.min(weights.TE, 0.08);
		} else if (phase === 'early' || phase === 'earlyMid') {
			weights.WR = Math.max(weights.WR, 0.12);
		}
	}
	if (primary === 'QB' && phase === 'late') {
		weights.QB = 0.58;
		weights.WR = Math.max(weights.WR, 0.12);
	}

	return normalizeWeights(weights);
}

function secondaryPositions(weights, primary) {
	return POSITIONS.filter((pos) => pos !== primary)
		.sort((a, b) => weights[b] - weights[a])
		.slice(0, 2);
}

function tierForPosition(position, phase) {
	if (phase === 'early' || phase === 'earlyMid') return TIER_GUIDANCE[position].high;
	if (phase === 'mid' || phase === 'lateMid') return TIER_GUIDANCE[position].mid;
	return TIER_GUIDANCE[position].low;
}

function buildRoundAction(
	primary,
	secondaries,
	weights,
	phase,
	spectrumValue,
	roundMeta = {},
	totalRounds = 18,
	round = 1,
	context = {}
) {
	const {
		format = 'best_ball',
		cumulativeBefore = emptyCounts(),
		lateQB = false,
		lateTE = false,
		constructionMode = 'zero'
	} = context;
	const heroMode = constructionMode === 'hero';
	const primaryPct = Math.round(weights[primary] * 100);
	const isRbHeavy = spectrumValue < 40;
	const isWrHeavy = spectrumValue > 60;
	const revisitLen = revisitRoundCount(totalRounds);
	const lateTeWindow = lateTERoundWindow(totalRounds);
	const lateQbWindow = lateQBRoundWindow(totalRounds);

	if (isHeroRbAnchorMeta(roundMeta)) {
		return `Hero RB anchor — lock RB1 in round 1 (${primaryPct}% focus). Do not draft another RB until round ${roundMeta.heroPauseUntilRound}; build WR/QB/TE through the gap.`;
	}

	if (isHeroWrAnchorMeta(roundMeta)) {
		return `Hero WR anchor — lock WR1 in round 1 (${primaryPct}% focus). Do not draft another WR until round ${roundMeta.heroPauseUntilRound}; build RB/QB/TE through the gap.`;
	}

	if (isHeroRbRevisitMeta(roundMeta)) {
		return `Revisit RB — hero RB1 is locked from round 1. Re-attack RB value here; catch up toward your ${TIER_GUIDANCE.RB.mid.toLowerCase()} depth targets.`;
	}

	if (isHeroWrRevisitMeta(roundMeta)) {
		return `Revisit WR — hero WR1 is locked from round 1. Re-attack WR value here; stack spike paths without forcing another early WR.`;
	}

	if (
		heroMode &&
		roundMeta.heroRbPaused &&
		!roundMeta.isElitePick &&
		round < roundMeta.heroPauseUntilRound
	) {
		return `Hero RB build — avoid RB before round ${roundMeta.heroPauseUntilRound}; prioritize WR, QB, and TE while tiers are intact.`;
	}

	if (
		heroMode &&
		roundMeta.heroWrPaused &&
		!roundMeta.isElitePick &&
		round < roundMeta.heroPauseUntilRound
	) {
		return `Hero WR build — avoid WR before round ${roundMeta.heroPauseUntilRound}; prioritize RB, QB, and TE while tiers are intact.`;
	}

	if (
		lateTE &&
		!isLateTERoundMeta(roundMeta) &&
		!roundMeta.isHeroRbOpen &&
		!roundMeta.isHeroWrOpen &&
		!roundMeta.isElitePick &&
		round < lateTeWindow.low
	) {
		return `Late TE build — avoid TE before rounds ${lateTeWindow.low}–${lateTeWindow.high}; three TEs land in that window with a late-half bias.`;
	}

	if (
		lateQB &&
		!isLateQBRoundMeta(roundMeta) &&
		!roundMeta.isHeroRbOpen &&
		!roundMeta.isHeroWrOpen &&
		!roundMeta.isElitePick &&
		round < lateQbWindow.low
	) {
		return `Late QB build — avoid QB before rounds ${lateQbWindow.low}–${lateQbWindow.high}; three QBs land in that window with an early-half bias.`;
	}

	if (roundMeta.isHeroRbOpen) {
		return `Hero RB window — lock RB${heroRbOpenCount(spectrumValue) >= 4 ? '-RB-RB-RB' : ''} to open the draft. WR comes in volume later; do not reach WR here.`;
	}

	if (roundMeta.isHeroWrOpen) {
		const opens = heroWrOpenCount(spectrumValue);
		const tail =
			isExtremeZeroRb(spectrumValue) && opens >= 6
				? ` RB run starts round ${opens + 1}; WR7 on round ${totalRounds} (final pick).`
				: ` RB run starts round ${opens + 1}; only 1 late WR for your 7th.`;
		return `Hero WR window — lock ${opens} WR${opens >= 4 ? ' in a row' : ''} to open (rounds 1–${opens}).${tail}`;
	}

	if (roundMeta.isElitePick) {
		if (roundMeta.eliteType === 'QB') {
			const revisitFocus = spectrumValue <= 20 ? 'WR' : isRbHeavy ? 'RB' : isWrHeavy ? 'WR' : 'RB/WR';
			return `Elite QB tier break — secure a top-4 QB with weekly floor (rushing preferred). Next ${revisitLen} rounds: refocus on ${revisitFocus} to stay on build pace.`;
		}
		if (roundMeta.eliteType === 'TE') {
			return `Elite TE tier break — pay for top-3 TE target share. Next ${revisitLen} rounds: refocus on ${spectrumValue <= 20 ? 'WR' : 'RB/WR'} before adding secondary TE/QB.`;
		}
	}

	if (primary === 'WR' && (isHeroWrRevisitMeta(roundMeta) || roundMeta.isRevisitRound)) {
		const anchorRound = roundMeta.revisitAfterRound;
		const elite = roundMeta.revisitAfterElite;
		if (isHeroWrRevisitMeta(roundMeta)) {
			return `Revisit WR — re-attack WR value after hero WR1; stack paths without forcing WR2 here.`;
		}
		if (elite) {
			return `Revisit WR — re-attack WR value after elite ${elite} (R${anchorRound}); do not double-dip ${elite} unless BPA clears by a full tier.`;
		}
		return `Revisit WR — re-attack WR value in this revisit window.`;
	}
	if (primary === 'RB' && (isHeroRbRevisitMeta(roundMeta) || roundMeta.isRevisitRound)) {
		const anchorRound = roundMeta.revisitAfterRound;
		const elite = roundMeta.revisitAfterElite;
		if (isHeroRbRevisitMeta(roundMeta)) {
			return `Revisit RB — re-attack RB value after hero RB1; catch up toward ${TIER_GUIDANCE.RB.mid.toLowerCase()} depth.`;
		}
		if (elite) {
			return `Revisit RB — re-attack RB value after elite ${elite} (R${anchorRound}); do not double-dip ${elite} unless BPA clears by a full tier.`;
		}
		return `Revisit RB — re-attack RB value in this revisit window.`;
	}
	if (roundMeta.isRevisitRound && !isHeroRbRevisitMeta(roundMeta) && !isHeroWrRevisitMeta(roundMeta)) {
		const elite = roundMeta.revisitAfterElite;
		const anchorRound = roundMeta.revisitAfterRound;
		return `Revisit ${primary} — re-attack ${primary} value after elite ${elite} (R${anchorRound}); stay on build pace.`;
	}

	if (primary === 'RB' && phase === 'early') {
		return isRbHeavy
			? `Lock RB workload (${primaryPct}% focus). Only pivot for a top-tier WR fall.`
			: `Secure RB floor if value exists; do not reach past equivalent WR.`;
	}
	if (primary === 'WR' && phase === 'early') {
		if (shouldEmphasizeBestBallWr(spectrumValue, format, round, cumulativeBefore, roundMeta)) {
			return `Best ball WR stack — ${BEST_BALL_STARTER_SLOTS.RB} RBs are on roster; prioritize WR target share and spike paths through round 10 (${primaryPct}% WR focus).`;
		}
		return isWrHeavy
			? `Take WR target share (${primaryPct}% focus). RB can wait if WR room is thinning.`
			: `Add WR talent if BPA; avoid forcing WR if RB value clears by a tier.`;
	}
	if (primary === 'RB' && (phase === 'mid' || phase === 'lateMid')) {
		return isWrHeavy
			? `Start RB run — target ${tierForPosition('RB', phase)} profiles before the tier cliff.`
			: `Continue RB depth; prioritize pass-down and goal-line equity.`;
	}
	if (primary === 'WR' && (phase === 'mid' || phase === 'lateMid')) {
		if (shouldEmphasizeBestBallWr(spectrumValue, format, round, cumulativeBefore, roundMeta)) {
			return `Best ball WR volume — keep adding WR ceiling while tiers are intact; flex is WR-heavy (${primaryPct}% focus).`;
		}
		return isRbHeavy
			? `WR volume round — stack ${secondaries[0] || 'TE'} correlation where it improves weekly ceiling.`
			: `Fill WR flex depth; prefer spike-week profiles over safe floor-only plays.`;
	}
	if (
		primary === 'WR' &&
		phase === 'late' &&
		isBestBallFormat(format) &&
		round >= totalRounds - 3
	) {
		return 'Best ball late dart — 1–2 WR (or RB) with spike-week paths; avoid safe floor-only profiles this late.';
	}
	if (
		primary === 'RB' &&
		isBestBallFormat(format) &&
		isBalancedSpectrum(spectrumValue) &&
		cumulativeBefore.RB < BEST_BALL_STARTER_SLOTS.RB &&
		round <= 4
	) {
		return `Best ball foundation — secure RB${BEST_BALL_STARTER_SLOTS.RB} before the WR push in rounds 3–10 (${primaryPct}% RB focus).`;
	}
	if (primary === 'TE') {
		const lateWindow = lateTERoundWindow(totalRounds);
		if (isLateTERoundMeta(roundMeta)) {
			return `Late TE — rounds ${lateWindow.low}–${lateWindow.high}; three TEs spaced ${lateWindow.low}–${lateWindow.high} with a late-half bias (${TIER_GUIDANCE.TE.mid.toLowerCase()}).`;
		}
		const teRounds = nonEliteTE1RoundWindow(totalRounds);
		const [tePickLow, tePickHigh] = nonEliteTE1PickRange(totalRounds);
		if (roundMeta.isAnchorWindow && roundMeta.anchorType === 'TE1') {
			return `TE1 window — pick ${tePickLow}–${tePickHigh} (~rounds ${teRounds.low}–${teRounds.high}, 12-team). Target ${TIER_GUIDANCE.TE.starter.toLowerCase()}; do not reach earlier.`;
		}
		if (roundMeta.isAnchorWindow && roundMeta.anchorType === 'TE2') {
			const r2 = extremeScheduleRoundFrom18(14, totalRounds);
			return `TE2 window — ~round ${r2} on ${totalRounds}-pick builds (${TIER_GUIDANCE.TE.mid.toLowerCase()}).`;
		}
		if (phase === 'late' || phase === 'lateMid') {
			return 'Backup TE — athletic profile, vacated targets, or stack after your first TE window.';
		}
		if (
			Math.abs(round - nonEliteFirstTERound(totalRounds)) <= 1 ||
			(round >= teRounds.low && round <= teRounds.high)
		) {
			return `TE1 window — pick ${tePickLow}–${tePickHigh} (~rounds ${teRounds.low}–${teRounds.high}, 12-team). Target ${TIER_GUIDANCE.TE.starter.toLowerCase()}; do not reach earlier.`;
		}
		return phase === 'early' || phase === 'earlyMid'
			? 'Take TE only on a clear tier break — you are capped at 3 TE max.'
			: 'Late TE: target athletic profile, vacated targets, or stack partner.';
	}
	if (primary === 'QB') {
		if (isLateQBRoundMeta(roundMeta)) {
			return `Late QB — rounds ${lateQbWindow.low}–${lateQbWindow.high}; three QBs spaced ${lateQbWindow.low}–${lateQbWindow.high} with an early-half bias (${TIER_GUIDANCE.QB.mid.toLowerCase()}).`;
		}
		if (roundMeta.isAnchorWindow && roundMeta.anchorType?.startsWith('QB')) {
			const qbNum = Number(roundMeta.anchorType.replace('QB', '')) - 1;
			const [pickLow, pickHigh] =
				NON_ELITE_QB_PICK_WINDOWS[qbNum] ||
				NON_ELITE_QB_PICK_WINDOWS[NON_ELITE_QB_PICK_WINDOWS.length - 1];
			const qbWindow = picksToRoundRange(pickLow, pickHigh, totalRounds);
			return `${roundMeta.anchorType} window — pick ${pickLow}–${pickHigh} (~rounds ${qbWindow.low}–${qbWindow.high}, 12-team).`;
		}
		const qb1Window = picksToRoundRange(
			NON_ELITE_QB_PICK_WINDOWS[0][0],
			NON_ELITE_QB_PICK_WINDOWS[0][1],
			totalRounds
		);
		const qb2Window = picksToRoundRange(
			NON_ELITE_QB_PICK_WINDOWS[1][0],
			NON_ELITE_QB_PICK_WINDOWS[1][1],
			totalRounds
		);
		const qb3Window = picksToRoundRange(
			NON_ELITE_QB_PICK_WINDOWS[2][0],
			NON_ELITE_QB_PICK_WINDOWS[2][1],
			totalRounds
		);
		const qb1Round = nonEliteQBRoundByIndex(0, 3, totalRounds, spectrumValue);

		if (round >= qb3Window.low && round <= qb3Window.high) {
			return `QB3 window — pick ${NON_ELITE_QB_PICK_WINDOWS[2][0]}–${NON_ELITE_QB_PICK_WINDOWS[2][1]} (12-team), ~rounds ${qb3Window.low}–${qb3Window.high}. Late dart with rushing floor, stack, or bring-back.`;
		}
		if (round >= qb2Window.low && round <= qb2Window.high) {
			return `QB2 window — pick ${NON_ELITE_QB_PICK_WINDOWS[1][0]}–${NON_ELITE_QB_PICK_WINDOWS[1][1]} (12-team), ~rounds ${qb2Window.low}–${qb2Window.high}. Take your second QB here (streamer with floor or stack upside).`;
		}
		if (Math.abs(round - qb1Round) <= 1 || (round >= qb1Window.low && round <= qb1Window.high)) {
			return `QB1 window — pick ${NON_ELITE_QB_PICK_WINDOWS[0][0]}–${NON_ELITE_QB_PICK_WINDOWS[0][1]} (12-team), ~rounds ${qb1Window.low}–${qb1Window.high}. Lock your starter; QB2 targets picks ${NON_ELITE_QB_PICK_WINDOWS[1][0]}–${NON_ELITE_QB_PICK_WINDOWS[1][1]}.`;
		}
		return `Non-elite QB path — QB1 picks ${NON_ELITE_QB_PICK_WINDOWS[0][0]}–${NON_ELITE_QB_PICK_WINDOWS[0][1]}, QB2 ${NON_ELITE_QB_PICK_WINDOWS[1][0]}–${NON_ELITE_QB_PICK_WINDOWS[1][1]}, QB3 ${NON_ELITE_QB_PICK_WINDOWS[2][0]}–${NON_ELITE_QB_PICK_WINDOWS[2][1]} (12-team).`;
	}

	return `Primary ${primary} (${primaryPct}%). Acceptable pivots: ${secondaries.join(', ')}.`;
}

function buildPivotNote(primary, weights, spectrumValue, cumulative, target, roundMeta = {}, context = {}) {
	const { format = 'best_ball', cumulativeBefore = emptyCounts() } = context;
	const wrWeight = weights.WR;
	const rbWeight = weights.RB;

	if (roundMeta.isElitePick && roundMeta.eliteType) {
		const total = POSITIONS.reduce((acc, pos) => acc + target[pos], 0);
		return `After this pick: next ${revisitRoundCount(total)} rounds prioritize build core before more ${roundMeta.eliteType}.`;
	}

	if (roundMeta.isRevisitRound) {
		return `Pivot only if ${primary} tier is flat — elite ${roundMeta.revisitAfterElite} already locked; catch up on ${primary} count vs. target.`;
	}

	if (primary === 'RB' && wrWeight > 0.35) {
		return spectrumValue > 55
			? `Pivot: elite WR if RB room is on pace (${cumulative.RB}/${target.RB} RB).`
			: 'Pivot: WR with spike weeks if RB tier is flat.';
	}
	if (primary === 'WR' && rbWeight > 0.35) {
		if (
			shouldEmphasizeBestBallWr(
				spectrumValue,
				format,
				context.round || cumulative.RB,
				cumulativeBefore,
				roundMeta
			)
		) {
			return `Pivot: only for tier-1 RB value — you are in the best-ball WR stack window (${cumulative.WR}/${target.WR} WR).`;
		}
		return spectrumValue < 45
			? `Pivot: RB if behind pace (${cumulative.RB}/${target.RB} RB target).`
			: 'Pivot: RB with contingent upside if WR room is set.';
	}
	if (primary === 'TE') {
		return cumulative.TE >= target.TE
			? 'TE quota met — pivot to WR/RB unless elite TE value.'
			: 'Pivot: WR/RB if no TE in the next tier on your board.';
	}
	if (primary === 'QB') {
		return cumulative.QB >= target.QB
			? 'QB quota met — do not draft a 4th QB.'
			: 'Pivot: best available skill position unless top-8 QB.';
	}
	return null;
}

function interpolateHeroMeta(balancedMeta, spectrumValue) {
	if (spectrumValue <= 50) {
		const t = spectrumValue / 50;
		const rbMeta = ANCHOR_META.heroRb;
		return {
			label:
				t < 0.35 ? rbMeta.label : t > 0.65 ? balancedMeta.label : blendLabel(rbMeta.label, balancedMeta.label, t),
			shortLabel: t < 0.5 ? rbMeta.shortLabel : balancedMeta.shortLabel,
			spectrumLabel:
				spectrumValue <= 15
					? 'Hero RB'
					: spectrumValue < 38
						? 'RB-leaning (hero)'
						: spectrumValue < 48
							? 'Balanced (hero RB lean)'
							: 'Balanced',
			philosophy: blendText(rbMeta.philosophy, balancedMeta.philosophy, t),
			rules: blendRules(rbMeta.rules, balancedMeta.rules, t)
		};
	}

	const t = (spectrumValue - 50) / 50;
	const wrMeta = ANCHOR_META.heroWr;
	return {
		label:
			t > 0.65 ? wrMeta.label : t < 0.35 ? balancedMeta.label : blendLabel(balancedMeta.label, wrMeta.label, t),
		shortLabel: t > 0.5 ? wrMeta.shortLabel : balancedMeta.shortLabel,
		spectrumLabel:
			spectrumValue > 85
				? 'Hero WR'
				: spectrumValue > 62
					? 'WR-leaning (hero)'
					: spectrumValue > 52
						? 'Balanced (hero WR lean)'
						: 'Balanced',
		philosophy: blendText(balancedMeta.philosophy, wrMeta.philosophy, t),
		rules: blendRules(balancedMeta.rules, wrMeta.rules, t)
	};
}

function interpolateMeta(zeroWrMeta, balancedMeta, zeroRbMeta, spectrumValue, options = {}) {
	if (isHeroConstructionMode(options)) {
		return interpolateHeroMeta(balancedMeta, spectrumValue);
	}

	if (spectrumValue <= 50) {
		const t = spectrumValue / 50;
		return {
			label: t < 0.35 ? zeroWrMeta.label : t > 0.65 ? balancedMeta.label : blendLabel(zeroWrMeta.label, balancedMeta.label, t),
			shortLabel: t < 0.5 ? zeroWrMeta.shortLabel : balancedMeta.shortLabel,
			spectrumLabel:
				spectrumValue <= 15
					? 'Extreme Zero WR'
					: spectrumValue < 38
						? 'RB-leaning'
						: spectrumValue < 48
							? 'Balanced (RB lean)'
							: 'Balanced',
			philosophy: blendText(zeroWrMeta.philosophy, balancedMeta.philosophy, t),
			rules: blendRules(zeroWrMeta.rules, balancedMeta.rules, t)
		};
	}

	const t = (spectrumValue - 50) / 50;
	return {
		label: t > 0.65 ? zeroRbMeta.label : t < 0.35 ? balancedMeta.label : blendLabel(balancedMeta.label, zeroRbMeta.label, t),
		shortLabel: t > 0.5 ? zeroRbMeta.shortLabel : balancedMeta.shortLabel,
		spectrumLabel:
			spectrumValue > 82
				? 'Zero RB'
				: spectrumValue > 62
					? 'WR-leaning'
					: spectrumValue > 52
						? 'Balanced (WR lean)'
						: 'Balanced',
		philosophy: blendText(balancedMeta.philosophy, zeroRbMeta.philosophy, t),
		rules: blendRules(balancedMeta.rules, zeroRbMeta.rules, t)
	};
}

function blendLabel(left, right, t) {
	if (t < 0.33) return left;
	if (t > 0.67) return right;
	return `${left} ↔ ${right}`;
}

function blendText(left, right, t) {
	if (t <= 0.15) return left;
	if (t >= 0.85) return right;
	return `${left.split('. ')[0]}. As you move toward center: ${right.split('. ')[0].toLowerCase()}.`;
}

function blendRules(leftRules, rightRules, t) {
	if (t <= 0.2) return leftRules;
	if (t >= 0.8) return rightRules;
	return [
		leftRules[0],
		rightRules[0],
		leftRules[1] || rightRules[1],
		rightRules[2] || leftRules[2]
	];
}

function cumulativeFromSchedule(schedule, throughRound) {
	const counts = emptyCounts();
	schedule.slice(0, throughRound).forEach((pos) => {
		counts[pos] += 1;
	});
	return counts;
}

function buildCheckpoints(schedule, totalRounds) {
	const checkpointRounds = [];
	for (let round = 3; round <= totalRounds; round += 3) {
		checkpointRounds.push(round);
	}
	if (checkpointRounds[checkpointRounds.length - 1] !== totalRounds) {
		checkpointRounds.push(totalRounds);
	}

	return checkpointRounds.map((round) => ({
		round,
		counts: cumulativeFromSchedule(schedule, round),
		label: `After round ${round}`
	}));
}

function isNonEliteTE1Round(round, totalRounds, roundMeta = {}) {
	if (roundMeta.isElitePick || isLateTERoundMeta(roundMeta)) return false;
	if (roundMeta.isAnchorWindow && roundMeta.anchorType === 'TE1') return true;
	const teRounds = nonEliteTE1RoundWindow(totalRounds);
	return round >= teRounds.low && round <= teRounds.high;
}

function tierForRound(
	primary,
	phase,
	roundMeta,
	round = 1,
	totalRounds = 18,
	prevRoundMeta = null,
	prevPrimary = null
) {
	const prev = prevRoundMeta || {};
	if (isHeroRbAnchorMeta(roundMeta)) {
		return `Hero RB — RB1 anchor; avoid RB until round ${roundMeta.heroPauseUntilRound}`;
	}
	if (isHeroWrAnchorMeta(roundMeta)) {
		return `Hero WR — WR1 anchor; avoid WR until round ${roundMeta.heroPauseUntilRound}`;
	}
	if (primary === 'WR' && (isHeroWrRevisitMeta(roundMeta) || roundMeta.isRevisitRound)) {
		const h = TIER_GUIDANCE.WR.high;
		if (isHeroWrRevisitMeta(roundMeta)) {
			const continued =
				prevPrimary === 'WR' && isHeroWrRevisitMeta(prev);
			return continued
				? `${h} — continued revisit (same window after R1 anchor)`
				: `${h} — revisit after hero WR anchor`;
		}
		if (roundMeta.revisitAfterElite) {
			const eliteCont =
				prevPrimary === 'WR' &&
				prev.isRevisitRound &&
				!isHeroWrRevisitMeta(prev) &&
				!isHeroRbRevisitMeta(prev) &&
				prev.revisitAfterElite === roundMeta.revisitAfterElite &&
				prev.revisitAfterRound === roundMeta.revisitAfterRound;
			return eliteCont
				? `${h} — continued refocus after elite ${roundMeta.revisitAfterElite} (R${roundMeta.revisitAfterRound})`
				: `${h} — revisit after elite ${roundMeta.revisitAfterElite} (R${roundMeta.revisitAfterRound})`;
		}
		return `${h} — revisit window`;
	}
	if (primary === 'RB' && (isHeroRbRevisitMeta(roundMeta) || roundMeta.isRevisitRound)) {
		const h = TIER_GUIDANCE.RB.high;
		if (isHeroRbRevisitMeta(roundMeta)) {
			const continued = prevPrimary === 'RB' && isHeroRbRevisitMeta(prev);
			return continued
				? `${h} — continued revisit (same window after R1 anchor)`
				: `${h} — revisit after hero RB anchor`;
		}
		if (roundMeta.revisitAfterElite) {
			const eliteCont =
				prevPrimary === 'RB' &&
				prev.isRevisitRound &&
				!isHeroWrRevisitMeta(prev) &&
				!isHeroRbRevisitMeta(prev) &&
				prev.revisitAfterElite === roundMeta.revisitAfterElite &&
				prev.revisitAfterRound === roundMeta.revisitAfterRound;
			return eliteCont
				? `${h} — continued refocus after elite ${roundMeta.revisitAfterElite} (R${roundMeta.revisitAfterRound})`
				: `${h} — revisit after elite ${roundMeta.revisitAfterElite} (R${roundMeta.revisitAfterRound})`;
		}
		return `${h} — revisit window`;
	}
	if (roundMeta.isRevisitRound && !isHeroRbRevisitMeta(roundMeta) && !isHeroWrRevisitMeta(roundMeta)) {
		const h = TIER_GUIDANCE[primary]?.high || primary;
		return `${h} — revisit after elite ${roundMeta.revisitAfterElite} (R${roundMeta.revisitAfterRound})`;
	}
	if (roundMeta.isHeroRbOpen) {
		return 'Hero RB — RB1 / elite workload (Zero WR open)';
	}
	if (roundMeta.isHeroWrOpen) {
		return 'Hero WR — WR1 / alpha target share (Zero RB open)';
	}
	if (roundMeta.isElitePick && roundMeta.eliteType === 'QB') {
		return 'Elite QB — top-4 with weekly floor / rushing equity';
	}
	if (roundMeta.isElitePick && roundMeta.eliteType === 'TE') {
		return 'Elite TE — top-3 target share (tier break only)';
	}
	if (primary === 'TE' && roundMeta.isAnchorWindow && roundMeta.anchorType === 'TE2') {
		return TIER_GUIDANCE.TE.mid;
	}
	if (primary === 'TE' && isNonEliteTE1Round(round, totalRounds, roundMeta)) {
		return TIER_GUIDANCE.TE.starter;
	}
	if (primary === 'TE' && isLateTERoundMeta(roundMeta)) {
		return TIER_GUIDANCE.TE.mid;
	}
	if (primary === 'QB' && isLateQBRoundMeta(roundMeta)) {
		return TIER_GUIDANCE.QB.mid;
	}
	return tierForPosition(primary, phase);
}

const SCORING_VALUES = new Set(['half_ppr', 'full_ppr', 'tep', 'standard']);

export function normalizeScoring(scoring) {
	return SCORING_VALUES.has(scoring) ? scoring : 'half_ppr';
}

/**
 * @param {number} spectrumValue UI slider: zero mode 0 = Zero RB, 50 = Balanced, 100 = Zero WR; hero unchanged
 * @param {number} totalRounds
 * @param {{ eliteQB?: boolean, eliteTE?: boolean, lateQB?: boolean, lateTE?: boolean, format?: string, scoring?: string }} options
 */
export function buildDraftPlan(spectrumValue, totalRounds = 18, options = {}) {
	const displaySpectrumValue = clamp(spectrumValue, 0, 100);
	const rounds = clamp(totalRounds, 15, 20);
	const constructionMode = normalizeConstructionMode(options.constructionMode);
	const value = toEngineSpectrumValue(displaySpectrumValue, constructionMode);
	const eliteQB = Boolean(options.eliteQB && !options.lateQB);
	const eliteTE = Boolean(options.eliteTE && !options.lateTE);
	const lateQB = Boolean(options.lateQB && !options.eliteQB);
	const lateTE = Boolean(options.lateTE && !options.eliteTE);
	const format = normalizeFormat(options.format);
	const scoring = normalizeScoring(options.scoring);
	const buildOptions = { eliteQB, eliteTE, lateQB, lateTE, format, constructionMode };

	const rosterProjection = getTargetComposition(value, rounds, buildOptions);
	const {
		schedule,
		roundMeta,
		eliteAnchors,
		revisitRoundList,
		lateTERounds,
		lateQBRounds,
		heroRevisitRounds,
		heroPauseUntil
	} = buildPickSchedule(rosterProjection, value, rounds, buildOptions);
	const compositionLine = formatCompositionLine(rosterProjection);
	const meta = enrichMetaWithExecutionRules(
		interpolateMeta(ANCHOR_META.zeroWr, ANCHOR_META.balanced, ANCHOR_META.zeroRb, value, buildOptions),
		{
			spectrumValue: value,
			totalRounds: rounds,
			format,
			constructionMode,
			eliteQB,
			eliteTE,
			lateQB,
			lateTE,
			compositionLine,
			lateQBRounds,
			lateTERounds
		}
	);

	const roundsDetail = schedule.map((primary, index) => {
		const round = index + 1;
		const phase = getPhaseForRound(round, rounds);
		const pickMeta = roundMeta[index] || {};
		const cumulativeBefore = cumulativeFromSchedule(schedule, Math.max(0, round - 1));
		const pickContext = { format, cumulativeBefore, round, lateQB, lateTE, constructionMode };
		const weights = weightsForScheduledPick(primary, round, rounds, value, pickMeta, pickContext);
		const secondaries = secondaryPositions(weights, primary);
		const cumulative = cumulativeFromSchedule(schedule, round);

		const revLabel = revisitPositionLabel(primary, pickMeta);
		const prevPickMeta = index > 0 ? roundMeta[index - 1] || {} : null;
		const prevPrimarySlot = index > 0 ? schedule[index - 1] : null;

		return {
			round,
			phase: phase.phase,
			phaseLabel: pickMeta.isHeroRbAnchor
				? 'Hero RB'
				: pickMeta.isHeroWrAnchor
					? 'Hero WR'
					: revLabel
						? phase.label
						: pickMeta.isHeroRbOpen
							? 'Hero RB'
							: pickMeta.isHeroWrOpen
								? 'Hero WR'
								: pickMeta.isLateTE
									? 'Late TE'
									: pickMeta.isLateQB
										? 'Late QB'
										: pickMeta.isElitePick
											? `Elite ${pickMeta.eliteType}`
											: phase.label,
			weights,
			primary,
			secondaries,
			revisitPositionLabel: revLabel,
			tier: tierForRound(primary, phase.phase, pickMeta, round, rounds, prevPickMeta, prevPrimarySlot),
			action: buildRoundAction(
				primary,
				secondaries,
				weights,
				phase.phase,
				value,
				pickMeta,
				rounds,
				round,
				pickContext
			),
			pivot: buildPivotNote(primary, weights, value, cumulative, rosterProjection, pickMeta, pickContext),
			cumulative,
			isElitePick: Boolean(pickMeta.isElitePick),
			isRevisitRound: Boolean(
				pickMeta.isRevisitRound && !pickMeta.isHeroRbRevisit && !pickMeta.isHeroWrRevisit
			),
			isHeroRbOpen: Boolean(pickMeta.isHeroRbOpen),
			isHeroWrOpen: Boolean(pickMeta.isHeroWrOpen),
			isHeroRbAnchor: Boolean(pickMeta.isHeroRbAnchor),
			isHeroWrAnchor: Boolean(pickMeta.isHeroWrAnchor),
			isHeroRbRevisit: Boolean(pickMeta.isHeroRbRevisit),
			isHeroWrRevisit: Boolean(pickMeta.isHeroWrRevisit),
			heroPauseUntilRound: pickMeta.heroPauseUntilRound || null,
			isLateTE: Boolean(pickMeta.isLateTE),
			isLateQB: Boolean(pickMeta.isLateQB),
			eliteType: pickMeta.eliteType || null
		};
	});

	const checkpoints = buildCheckpoints(schedule, rounds);

	return {
		spectrumValue: displaySpectrumValue,
		totalRounds: rounds,
		meta,
		rosterProjection,
		compositionLine,
		rounds: roundsDetail,
		checkpoints,
		spectrumPosition: displaySpectrumValue / 100,
		constructionMode,
		eliteQB,
		eliteTE,
		lateQB,
		lateTE,
		format,
		scoring,
		eliteAnchors,
		revisitRoundList,
		lateTERounds,
		lateQBRounds,
		heroRevisitRounds,
		heroPauseUntil
	};
}

export function formatCompositionLine(counts) {
	return POSITIONS.map((pos) => counts[pos]).join('-');
}

export function formatRosterCounts(counts) {
	return POSITIONS.map((pos) => `${counts[pos]} ${pos}`).join(' · ');
}
